// ─── Shared shapes ────────────────────────────────────────────────────────────

// A point or an offset, in whichever space the caller is working in.
interface Vec { x: number; y: number }

// An axis-aligned rectangle, as the interface lays its furniture out.
interface Rect extends Vec { w: number; h: number }

// A pair of coordinates as a tuple, for the canvas calls that spread them.
type Point = [number, number];

// A distance in from each edge of something rectangular.
interface Insets { top: number; right: number; bottom: number; left: number }

// The element at an index that the surrounding code guarantees is in range:
// a loop over the array's own length, a clamped sample, a tuple position the
// checker cannot see. Under noUncheckedIndexedAccess a plain read is typed as
// possibly undefined; this says the invariant out loud, and throws rather than
// carries an undefined onward if it is ever broken.
function at<T>(items: readonly T[], index: number): T {
  const item = items[index];
  if (item === undefined) throw new RangeError(`index ${index} is out of range (length ${items.length})`);
  return item;
}

// ─── Canvas ───────────────────────────────────────────────────────────────────
function findCanvas(): HTMLCanvasElement {
  const element = document.getElementById('gameCanvas');
  if (!(element instanceof HTMLCanvasElement)) throw new Error('index.html has no canvas with id gameCanvas');
  return element;
}

function contextOf(target: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = target.getContext('2d');
  if (context === null) throw new Error('the browser refused a 2D canvas context');
  return context;
}

const canvas: HTMLCanvasElement = findCanvas();
const ctx: CanvasRenderingContext2D = contextOf(canvas);

// ─── View ─────────────────────────────────────────────────────────────────────
// The game is laid out in a fixed viewport and scaled to fit whatever window
// it is given. Every player then walks the same hills and climbs the same
// distance, so one run is comparable with another; the window only decides how
// large that viewport is drawn and how much letterboxing surrounds it. A window
// at least as wide as it is tall sees the same amount of world too, while an
// upright one sees the middle slice of it (see viewTransform). 16:9 is the
// common case, so most windows get no bars at all.
const VIEW_W: number = 1920;
const VIEW_H: number = 1080;

// The world fills the viewport, but the interface is laid out in a fixed space
// of its own and scaled up to meet it. Widening the player's view of the world
// then costs nothing in legibility: the clock, the meter and the start screen
// keep the size they always had, and only the world gets further away.
const UI_SCALE: number = VIEW_W / 1280;
const UI_REF_W: number = VIEW_W / UI_SCALE;   // 1280, the width the interface is designed at
const UI_REF_H: number = VIEW_H / UI_SCALE;   // 720, likewise its height
// Upright, the interface takes the window instead, and this is the narrowest it
// is allowed to get: the start panel plus a margin either side.
const UI_MIN_W: number = 620;

// The backing store is the window's size times the device pixel ratio, so a
// phone's screen is drawn at its own resolution rather than upscaled from a
// blurry one, while the canvas is still laid out at the window's CSS size. The
// ratio is capped to bound the fill cost on a dense screen. Everything measured
// in "canvas pixels" below is measured in this backing store.
const MAX_DPR: number = 2;
let dpr: number = 1;

// What a notch or a home indicator takes off each edge of the glass, in CSS
// pixels. viewport-fit=cover lets the page reach under them, so the world is
// drawn there and only the interface keeps clear. index.html exposes the insets
// as custom properties; where they cannot be read, nothing is inset.
const safeInsets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
function readSafeInsets(): void {
  try {
    const style = window.getComputedStyle(document.documentElement);
    for (const side of ['top', 'right', 'bottom', 'left'] as const) {
      safeInsets[side] = parseFloat(style.getPropertyValue(`--safe-${side}`)) || 0;
    }
  } catch {
    // No computed styles here (the test page): nothing is inset.
  }
}

function fitCanvas(): void {
  dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
  canvas.width  = Math.round(window.innerWidth  * dpr);
  canvas.height = Math.round(window.innerHeight * dpr);
  canvas.style.width  = `${window.innerWidth}px`;
  canvas.style.height = `${window.innerHeight}px`;
  readSafeInsets();
}
fitCanvas();
window.addEventListener('resize', fitCanvas);

// Uniform scale and centring offset taking viewport pixels to canvas pixels.
//
// A window taller than it is wide — a phone held upright — is matched on its
// height rather than contained inside it. Letterboxing one leaves the world a
// thin band across the middle with three quarters of the glass unused; matching
// the height fills it, and the sides of the viewport run off the screen instead,
// leaving a tall slice of the world through the middle. Every other window shape
// is contained exactly as before.
function isUpright(): boolean {
  return canvas.height > canvas.width;
}

// A uniform scale and an offset: where one coordinate space sits in another.
interface Transform { scale: number; x: number; y: number }

// The interface's transform also says how large the interface is, in its own
// units, since that depends on the window (see uiTransform).
interface UiTransform extends Transform { w: number; h: number }

function viewTransform(): Transform {
  const scale = isUpright()
    ? canvas.height / VIEW_H
    : Math.min(canvas.width / VIEW_W, canvas.height / VIEW_H);
  return {
    scale,
    x: (canvas.width  - VIEW_W * scale) / 2,
    y: (canvas.height - VIEW_H * scale) / 2,
  };
}

// How much of the viewport's width actually reaches the glass. The whole of it
// on a window that contains the viewport; the middle slice on an upright one.
function visibleViewW(): number {
  return Math.min(VIEW_W, canvas.width / viewTransform().scale);
}

// Where the interface is laid out. Contained windows put it in the viewport
// exactly as they always have. An upright window cannot: the viewport overflows
// the glass, and a clock or a hint anchored to its edge would go over the side
// with it, so there the interface takes the window instead — as tall as the
// window makes it, and never narrower than the start panel needs.
function uiTransform(): UiTransform {
  const t = viewTransform();
  if (!isUpright()) {
    return { scale: t.scale * UI_SCALE, x: t.x, y: t.y, w: UI_REF_W, h: UI_REF_H };
  }
  const scale = Math.min(canvas.height / UI_REF_H, canvas.width / UI_MIN_W);
  return { scale, x: 0, y: 0, w: canvas.width / scale, h: canvas.height / scale };
}

function uiW(): number { return uiTransform().w; }
function uiH(): number { return uiTransform().h; }

// The safe-area insets in interface units, less whatever letterbox bar already
// keeps the interface clear of that edge. Anything anchored to an edge of the
// interface stands off it by this much more.
function uiInsets(): Insets {
  const t = uiTransform();
  const barRight  = canvas.width  - (t.x + t.w * t.scale);
  const barBottom = canvas.height - (t.y + t.h * t.scale);
  return {
    top:    Math.max(0, safeInsets.top    * dpr - t.y)       / t.scale,
    right:  Math.max(0, safeInsets.right  * dpr - barRight)  / t.scale,
    bottom: Math.max(0, safeInsets.bottom * dpr - barBottom) / t.scale,
    left:   Math.max(0, safeInsets.left   * dpr - t.x)       / t.scale,
  };
}

// Browser client coordinates into canvas pixels. Events are reported in CSS
// pixels, and the backing store is dpr times that.
function toCanvas(clientX: number, clientY: number): Vec {
  const rect = canvas.getBoundingClientRect();
  return { x: (clientX - rect.left) * dpr, y: (clientY - rect.top) * dpr };
}

// Browser client coordinates into interface coordinates, for taps on buttons.
function toUI(clientX: number, clientY: number): Vec {
  const c = toCanvas(clientX, clientY);
  const t = uiTransform();
  return { x: (c.x - t.x) / t.scale, y: (c.y - t.y) / t.scale };
}

// Browser client coordinates back into viewport coordinates, for the pointer.
function toView(clientX: number, clientY: number): Vec {
  const c = toCanvas(clientX, clientY);
  const t = viewTransform();
  return { x: (c.x - t.x) / t.scale, y: (c.y - t.y) / t.scale };
}

// Paints the letterbox and the sky, then moves into viewport coordinates and
// clips to them. Balanced by endView() once the frame's overlays are drawn.
function beginView(): void {
  const t = viewTransform();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(t.scale, 0, 0, t.scale, t.x, t.y);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, VIEW_W, VIEW_H);
  ctx.clip();
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);
}

function endView(): void {
  ctx.restore();
}

// Interface coordinates, for anything that belongs to the player rather than
// to the world. Balanced by endUI().
function beginUI(): void {
  const t = uiTransform();
  ctx.save();
  ctx.setTransform(t.scale, 0, 0, t.scale, t.x, t.y);
}

function endUI(): void {
  ctx.restore();
}

const GROUND_RATIO: number   = 0.10;
const PLAYER_RADIUS: number  = 25;
const BOX_SIZE: number       = PLAYER_RADIUS * 2;
const MOVE_SPEED: number     = 6;
const ACCEL: number          = 1.5;
const FRICTION: number       = 1.2;
const JUMP_FORCE: number     = -12;
const GRAVITY: number        = 0.5;

// A beat between catching the tenth balloon and leaving the ground, so the
// pickup reads as its own moment before the world drops away.
const FLOAT_DELAY: number = 30;   // ticks, half a second

const BALLOON_STRING_LEN: number = 48;
const BALLOON_BUOYANCY: number   = 0.09;
const BALLOON_DRAG: number       = 0.97;
const BALLOON_RADIUS: number     = 12;

const TREE_SPACING_MIN: number = 100;
const TREE_SPACING_MAX: number = 240;
const BOX_SPACING_MIN: number  = 600;
const BOX_SPACING_MAX: number  = 1400;

const GULL_COUNT: number   = 10;
const GULL_SZ: number      = PLAYER_RADIUS * 0.7;           // drawing scale (~17.5px)
const GULL_ALT_MIN: number = PLAYER_RADIUS * 2 * 10;        // 10 player-heights above terrain
const GULL_ALT_MAX: number = PLAYER_RADIUS * 2 * 18;        // 18 player-heights above terrain
const GULL_SPD_MIN: number = 20 / 60;                       // 20 px/s in px/tick (~0.33)
const GULL_SPD_MAX: number = 480 / 60;                      // 480 px/s in px/tick (~8.00)

// ─── Terrain heightmap ────────────────────────────────────────────────────────
const TERRAIN_STEP: number   = 8;   // world px between stored samples
const MAX_SLOPE: number      = Math.tan(30 * Math.PI / 180); // tan(30°)
const KEEP_RANGE: number     = 5;   // screen-widths of terrain to keep on each side
const GEN_THRESHOLD: number  = 3;   // generate when within this many screen-widths of edge

let terrain: number[] = [];   // y values
let terrainStartX: number = 0;        // world x of terrain[0]

function terrainEndX(): number { return terrainStartX + terrain.length * TERRAIN_STEP; }

function terrainY(worldX: number): number {
  const exactIdx = (worldX - terrainStartX) / TERRAIN_STEP;
  const i0 = Math.floor(exactIdx);
  const i1 = i0 + 1;
  const a  = Math.max(0, Math.min(terrain.length - 1, i0));
  const b  = Math.max(0, Math.min(terrain.length - 1, i1));
  const t  = exactIdx - i0;
  const ya = at(terrain, a), yb = at(terrain, b);
  return ya + (yb - ya) * Math.max(0, Math.min(1, t));
}

function advanceSlope(slope: number, y: number, dir = 1): number {
  const base      = VIEW_H * (1 - GROUND_RATIO);
  const range     = VIEW_H * 0.28;
  const deviation = (y - base) / range;           // -1…1
  // dir = +1 for rightward, -1 for leftward — flips mean-reversion so it
  // always pulls the terrain back toward base regardless of traversal direction.
  slope += -deviation * 0.03 * dir
         + (Math.random() - 0.5) * 0.06;
  return Math.max(-MAX_SLOPE, Math.min(MAX_SLOPE, slope));
}

function extendTerrainRight(steps: number): void {
  let slope: number = terrain.length >= 2
    ? (at(terrain, terrain.length - 1) - at(terrain, terrain.length - 2)) / TERRAIN_STEP
    : 0;
  for (let i: number = 0; i < steps; i++) {
    const lastY = at(terrain, terrain.length - 1);
    slope = advanceSlope(slope, lastY);
    terrain.push(lastY + slope * TERRAIN_STEP);
  }
}

function extendTerrainLeft(steps: number): void {
  let slope: number = terrain.length >= 2
    ? (at(terrain, 1) - at(terrain, 0)) / TERRAIN_STEP
    : 0;
  const newPoints: number[] = [];
  let y: number = at(terrain, 0);
  for (let i: number = 0; i < steps; i++) {
    slope = advanceSlope(slope, y, -1);
    y = y - slope * TERRAIN_STEP;
    newPoints.unshift(y);
  }
  terrain = newPoints.concat(terrain);
  terrainStartX -= steps * TERRAIN_STEP;
}

function trimTerrain(): void {
  const keepPx = KEEP_RANGE * VIEW_W;
  // Trim left
  while (terrain.length > 2 && player.x - terrainStartX > keepPx) {
    terrain.shift();
    terrainStartX += TERRAIN_STEP;
  }
  // Trim right
  while (terrain.length > 2 && terrainEndX() - player.x > keepPx) {
    terrain.pop();
  }
}

function maintainTerrain(): void {
  const genPx = GEN_THRESHOLD * VIEW_W;
  const chunk = Math.ceil(VIEW_W / TERRAIN_STEP) + 10;
  if (terrainEndX() - player.x < genPx) extendTerrainRight(chunk);
  if (player.x - terrainStartX  < genPx) extendTerrainLeft(chunk);
  trimTerrain();
}

function initTerrain(): void {
  const base  = VIEW_H * (1 - GROUND_RATIO);
  terrainStartX = 0;
  terrain = [base];
  const initSteps = Math.ceil((KEEP_RANGE * VIEW_W) / TERRAIN_STEP);
  extendTerrainRight(initSteps);
  extendTerrainLeft(initSteps);
}

// ─── Objects ──────────────────────────────────────────────────────────────────

// A balloon still tied to its tree: an offset from the tree top, held by a
// string of the default length unless one is given.
interface TreeBalloon { dx: number; dy: number; vx: number; vy: number; stringLen?: number }

interface Tree { x: number; height: number; lean: number; balloon: TreeBalloon | null }

// A crate. It sits on the terrain, so its x is all that needs storing.
interface Box { x: number }

type GullState = 'flying' | 'gliding' | 'landing' | 'landed' | 'takingoff' | 'divebomb' | 'dead';

interface Gull {
  x: number;
  y: number;
  vx: number;
  vy: number;
  dir: number;              // +1 facing right, -1 facing left
  state: GullState;
  wingPhase: number;
  wingSpeed: number;
  stateTimer: number;
  landedDur: number;
  targetAlt: number;
  health: number;
  angle: number;
  angularVel: number;
  spawnAge: number;         // ticks since this gull was created (never resets)
  // Divebomb fields
  divePhase: number;        // 0=approach 1=dive 2=pullup 3=circle
  diveCooldown: number;     // ticks until allowed to pop again (900 = 15 s)
  diveAngle: number;        // chosen dive angle (rad, π/4–3π/4)
  diveTgt: Vec;             // world-space approach position
  diveBalloonIdx: number;   // which balloon is being targeted
  circleAngle: number;      // current angle in circling orbit
  circleDir: number;        // orbit rotation direction (fixed when entering phase 3)
  ragdoll: GullRagdoll | null;
}

const trees: Tree[] = [];
const boxes: Box[] = [];
const gulls: Gull[] = [];

function makeTreeAt(x: number): Tree {
  return {
    x,
    height:  80 + Math.floor(Math.random() * 60),
    lean:    Math.floor(Math.random() * 30) - 15,
    balloon: Math.random() < 0.10
      ? { dx: 0, dy: -BALLOON_STRING_LEN, vx: 0, vy: 0 }
      : null,
  };
}

function makeGull(x: number): Gull {
  const groundY = terrainY(x);
  const alt     = GULL_ALT_MIN + Math.random() * (GULL_ALT_MAX - GULL_ALT_MIN);
  const dir     = Math.random() < 0.5 ? 1 : -1;
  return {
    x, y:       groundY - alt,
    vx:         dir * (GULL_SPD_MIN + Math.random() * (GULL_SPD_MAX - GULL_SPD_MIN)),
    vy:         0,
    dir,
    state:      'flying',   // flying | gliding | landing | landed | takingoff | dead
    wingPhase:  Math.random() * Math.PI * 2,
    wingSpeed:  0.11 + Math.random() * 0.08,
    stateTimer: 0,
    landedDur:  0,
    targetAlt:  alt,
    health:     100,
    angle:      0,
    angularVel: 0,
    spawnAge:       0,    // ticks since this gull was created (never resets)
    // Divebomb fields
    divePhase:      0,    // 0=approach 1=dive 2=pullup 3=circle
    diveCooldown:   0,    // ticks until allowed to pop again (900 = 15 s)
    diveAngle:      0,    // chosen dive angle (rad, π/4–3π/4)
    diveTgt:        { x: 0, y: 0 }, // world-space approach position
    diveBalloonIdx: 0,    // which balloon is being targeted
    circleAngle:    0,    // current angle in circling orbit
    circleDir:      1,    // orbit rotation direction (fixed when entering phase 3)
    ragdoll:        null,
  };
}

// Population frontier: tracks how far objects have been placed on each side.
// Driven by camera movement — completely decoupled from terrain extension.
let popFrontierRight: number | null = null;
let popFrontierLeft:  number | null = null;
const POP_LOOKAHEAD: number  = 2; // screen-widths ahead of camera edge to populate
const TREE_MIN_GAP: number   = TREE_SPACING_MIN;
const BOX_MIN_GAP: number    = BOX_SIZE * 3;

// Poisson-distributed integer count for expected value lambda.
function poissonCount(lambda: number): number {
  if (lambda <= 0) return 0;
  // Knuth algorithm (exact for small lambda); Gaussian approx for large.
  if (lambda > 25) return Math.max(0, Math.round(lambda + (Math.random() - 0.5) * Math.sqrt(lambda) * 3));
  const L = Math.exp(-lambda);
  let p: number = 1, k: number = 0;
  do { k++; p *= Math.random(); } while (p > L);
  return k - 1;
}

// Populate a strip [leftX, rightX] with randomly-placed objects.
// Uses Poisson count for trees and boxes separately; enforces min-gap no-overlap.
function populateStrip(leftX: number, rightX: number): void {
  if (rightX <= leftX) return;
  const width = rightX - leftX;

  // Trees
  const avgTree   = (TREE_SPACING_MIN + TREE_SPACING_MAX) / 2;
  const numTrees  = poissonCount(width / avgTree);
  for (let i: number = 0; i < numTrees; i++) {
    const x = leftX + Math.random() * width;
    if (Math.abs(x) < 200) continue;
    if (!trees.some(t => Math.abs(t.x - x) < TREE_MIN_GAP)) trees.push(makeTreeAt(x));
  }

  // Boxes (sparser)
  const avgBox   = (BOX_SPACING_MIN + BOX_SPACING_MAX) / 2;
  const numBoxes = poissonCount(width / avgBox);
  for (let i: number = 0; i < numBoxes; i++) {
    const x = leftX + Math.random() * width;
    if (Math.abs(x) < 400) continue;
    if (!boxes.some(b => Math.abs(b.x - x) < BOX_MIN_GAP)) boxes.push({ x: Math.floor(x) });
  }
}

// Called each tick after camera is updated. Advances population frontiers to
// stay POP_LOOKAHEAD screen-widths ahead of the camera on each side.
function advancePopulation(): void {
  const lookPx    = POP_LOOKAHEAD * VIEW_W;
  const camLeft   = camera.x;
  const camRight  = camera.x + VIEW_W;

  if (popFrontierRight === null || popFrontierLeft === null) {
    // First call: populate the full initial 10-screen-width area at once.
    const initRange  = KEEP_RANGE * VIEW_W;
    popFrontierLeft  = camLeft  - initRange;
    popFrontierRight = camRight + initRange;
    populateStrip(popFrontierLeft, popFrontierRight);
    return;
  }

  if (camRight + lookPx > popFrontierRight) {
    const newRight = camRight + lookPx;
    populateStrip(popFrontierRight, newRight);
    popFrontierRight = newRight;
  }
  if (camLeft - lookPx < popFrontierLeft) {
    const newLeft = camLeft - lookPx;
    populateStrip(newLeft, popFrontierLeft);
    popFrontierLeft = newLeft;
  }
}

function cullObjects(): void {
  const cullPx = KEEP_RANGE * VIEW_W;
  let culledAnyLeft: boolean = false, culledAnyRight: boolean = false;

  for (let i: number = trees.length - 1; i >= 0; i--) {
    const tree = at(trees, i);
    if (Math.abs(tree.x - player.x) > cullPx) {
      if (tree.x < player.x) culledAnyLeft = true; else culledAnyRight = true;
      trees.splice(i, 1);
    }
  }
  for (let i: number = boxes.length - 1; i >= 0; i--) {
    const box = at(boxes, i);
    if (Math.abs(box.x - player.x) > cullPx) {
      if (box.x < player.x) culledAnyLeft = true; else culledAnyRight = true;
      boxes.splice(i, 1);
    }
  }

  // Roll frontiers back toward player when objects are culled on that side so
  // revisited areas get repopulated when the player returns.
  if (popFrontierRight !== null && popFrontierLeft !== null) {
    if (culledAnyLeft)  popFrontierLeft  = Math.max(popFrontierLeft,  player.x - cullPx);
    if (culledAnyRight) popFrontierRight = Math.min(popFrontierRight, player.x + cullPx);
  }
}

// ─── Camera / state ───────────────────────────────────────────────────────────
const camera: Vec = { x: 0, y: 0 };

// Where a walking player sits down the screen. Two thirds keeps the ground
// under them in comfortable view while leaving most of the frame for the sky
// above, which is where the balloons rise and the gulls circle.
const CAMERA_BIAS: number = 2 / 3;

// Snaps the view onto the player, at the resting offset update() aims for.
// The loop only moves the camera while a match is running, so without this the
// start screen would look at the world from the origin rather than from where
// the player is standing.
function centreCamera(): void {
  camera.x = player.x - VIEW_W / 2;
  camera.y = player.y - VIEW_H * CAMERA_BIAS;
}
let onGround: boolean  = false;
let touchingGround: boolean = false;    // feet on terrain or a box top — true while floating too
let floatAnchorY: number | null = null;   // altitude the current balloon ascent started from
let floatDelay: number = FLOAT_DELAY;  // ticks left before a full bunch takes the weight
type GroundSurface = 'grass' | 'box';
let groundSurface: GroundSurface = 'grass';   // what the player last stood on
let stepAccum: number = 0;             // distance walked since the last footstep
let isSkiing: boolean  = false;
let gameWon: boolean   = false;
let paused: boolean    = false;   // set when the window loses focus; cleared by a click
let gameStarted: boolean = false; // false until Start is clicked on the landing page
let skiAudio: SkiAudio | null = null;
let windTick: number  = 0;
let windNoise: number = 0;
let playerBalloons: RopeBalloon[] = [];
const METER_SECTIONS: number = 10;
let meterFill: number  = 0; // animated 0..1 fill for the balloon meter
let meterPulse: number = 0; // 1 → 0 burst on pickup
let mouseDown: boolean     = false;
let slingshotPull: number = 0;  // 0 = relaxed, 1 = full draw
let slingshotFlip: number    = 1;  // +1 = right-side hold, -1 = left-side (animates between)
let solvedAimAngle: number   = 0;  // current solver output (radians)
const balls: Ball[] = [];
interface Pop   { x: number; y: number; t: number }                  // a balloon pop animation
interface Burst { x: number; y: number; count: number; t: number }   // a multi-kill comic burst: two birds, one stone
const pops:   Pop[]   = [];
const bursts: Burst[] = [];
const POP_LIFE: number   = 25;    // ticks
const BURST_LIFE: number = 810;   // ticks, 13.5 s

// ─── Audio ────────────────────────────────────────────────────────────────────
// Safari before 14.1 only had the prefixed constructor. This merges into the
// global Window type (the file is a script, so there is no `declare global`),
// which the linter cannot see as a use.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Window { webkitAudioContext?: typeof AudioContext }

let audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext {
  audioCtx ??= new (window.webkitAudioContext ?? window.AudioContext)();
  return audioCtx;
}

// Unwires a finished sound from the output. Nothing in an audio graph does
// this for itself: a chain stays connected to the destination, and gets mixed
// every render quantum, until something disconnects it or the collector
// eventually reaches it. A match plays hundreds of one-shot sounds, and the
// dead ones piling up is heard on a phone first as the audio thread falling
// behind and then as no sound at all.
function releaseAfter(ac: AudioContext, node: AudioNode, endTime: number): void {
  setTimeout(() => { node.disconnect(); }, Math.max(0, (endTime - ac.currentTime) * 1000) + 80);
}

function playPickupSound(): void {
  const ac  = getAudioCtx();
  const t   = ac.currentTime;
  const dur = 0.42;

  // ── Treble whoosh: white noise through a bandpass that sweeps 480 → 1100 Hz ─
  const noise = ac.createBufferSource();
  noise.buffer = getRollBuf(ac);

  const bpf = ac.createBiquadFilter();
  bpf.type = 'bandpass';
  bpf.Q.value = 5;
  bpf.frequency.setValueAtTime(480, t);
  bpf.frequency.exponentialRampToValueAtTime(1100, t + dur);

  const whooshGain = ac.createGain();
  whooshGain.gain.setValueAtTime(0, t);
  whooshGain.gain.linearRampToValueAtTime(0.55, t + 0.04);
  whooshGain.gain.exponentialRampToValueAtTime(0.001, t + dur);

  noise.connect(bpf);
  bpf.connect(whooshGain);
  whooshGain.connect(ac.destination);
  noise.start(t, Math.random() * 1.5);
  noise.stop(t + dur);
  releaseAfter(ac, whooshGain, t + dur);

  // Pitched sine sliding in parallel to give tonal identity to the whoosh
  const sine     = ac.createOscillator();
  const sineGain = ac.createGain();
  sine.type = 'sine';
  sine.frequency.setValueAtTime(480, t);
  sine.frequency.exponentialRampToValueAtTime(1047, t + dur);
  sineGain.gain.setValueAtTime(0, t);
  sineGain.gain.linearRampToValueAtTime(0.10, t + 0.04);
  sineGain.gain.exponentialRampToValueAtTime(0.001, t + dur);
  sine.connect(sineGain);
  sineGain.connect(ac.destination);
  sine.start(t);
  sine.stop(t + dur);
  releaseAfter(ac, sineGain, t + dur);

  // ── Bassy undertone slide pulse (58 → 120 Hz) ──────────────────────────────
  const bass     = ac.createOscillator();
  const bassGain = ac.createGain();
  bass.type = 'sine';
  bass.frequency.setValueAtTime(58, t);
  bass.frequency.exponentialRampToValueAtTime(120, t + 0.22);
  bassGain.gain.setValueAtTime(0, t);
  bassGain.gain.linearRampToValueAtTime(0.48, t + 0.012);
  bassGain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
  bass.connect(bassGain);
  bassGain.connect(ac.destination);
  bass.start(t);
  bass.stop(t + 0.28);
  releaseAfter(ac, bassGain, t + 0.28);
}

function onBalloonPickup(): void {
  meterPulse = 1;
  playPickupSound();
}

// The looping ski noise, with the last volume and filter frequency sent to it
// (see setTargetIfChanged).
interface SkiAudio {
  source: AudioBufferSourceNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  lastVol: number;
  lastFreq: number;
}

function startSkiAudio(): void {
  if (skiAudio) return;
  const ac = getAudioCtx();

  const source = ac.createBufferSource();
  source.buffer = getRollBuf(ac);
  source.loop   = true;

  // Bandpass for the "shush" — frequency and gain driven by speed
  const filter = ac.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 150;
  filter.Q.value = 1.8;

  const gain = ac.createGain();
  gain.gain.value = 0;   // fade in handled by tickSkiAudio

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ac.destination);
  source.start();

  skiAudio = { source, filter, gain, lastVol: -1, lastFreq: -1 };
}

function fadeOutSkiAudio(): void {
  if (!skiAudio) return;
  const ac = getAudioCtx();
  const { gain, source } = skiAudio;
  skiAudio = null;                                       // detach immediately so tickSkiAudio ignores it
  gain.gain.setTargetAtTime(0, ac.currentTime, 0.22);   // ~0.65 s exponential fade
  source.stop(ac.currentTime + 1.2);                    // stop after fully faded
  releaseAfter(ac, gain, ac.currentTime + 1.2);
}

function tickSkiAudio(speed: number): void {
  if (!skiAudio) return;
  const ac   = getAudioCtx();
  const t    = ac.currentTime;
  const norm = Math.min(speed / 18, 1.0);               // 0→1 over 0–18 px/tick
  setTargetIfChanged(skiAudio.gain.gain,        skiAudio, 'lastVol',  norm * 0.26,        t, 0.10, 0.002);
  setTargetIfChanged(skiAudio.filter.frequency, skiAudio, 'lastFreq', 120 + norm * 1900,  t, 0.08, 5);
}

// ─── Ball audio ───────────────────────────────────────────────────────────────
let sharedRollBuf: AudioBuffer | null = null;
function getRollBuf(ac: AudioContext): AudioBuffer {
  if (sharedRollBuf) return sharedRollBuf;
  const len = ac.sampleRate * 2;
  sharedRollBuf = ac.createBuffer(1, len, ac.sampleRate);
  const d = sharedRollBuf.getChannelData(0);
  for (let i: number = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return sharedRollBuf;
}

// Returns {pan, vol} based on ball's screen position relative to player centre.
// Screen-edge objects are 50% volume; off-screen approach 0%. Measured against
// the width that actually reaches the glass, so a ball at the edge of an upright
// phone is hard left or right rather than barely off centre.
// Where a sound sits in the stereo field and how loud it is, both 0..1 or so.
interface AudioPosition { pan: number; vol: number }

function ballAudioPos(b: Vec): AudioPosition {
  const halfW = visibleViewW() / 2;
  const dx    = (b.x - camera.x) - VIEW_W  / 2;
  const dy    = (b.y - camera.y) - (player.y - camera.y);
  const pan   = Math.max(-1, Math.min(1, dx / halfW));
  const normX = dx / halfW;
  const normY = dy / (VIEW_H / 2);
  const vol   = Math.max(0, 1 - 0.5 * Math.sqrt(normX * normX + normY * normY));
  return { pan, vol };
}

// What a ball can roll along, and so which rolling noise it makes.
type RollSurface = 'ground' | 'box';

// One-shot impact sound. speed = |vn|.
function playBallBounce(b: Vec, surface: RollSurface | 'bird', speed: number): void {
  const ac  = getAudioCtx();
  const pos = ballAudioPos(b);
  const vol = pos.vol * Math.min(speed / 14, 1);
  if (vol < 0.06) return;
  const t = ac.currentTime;

  // Noise comes from the shared buffer at a random offset. Filling a fresh one
  // here cost a twentieth of a millisecond of Math.random on the main thread
  // for every bounce, in the middle of the physics step.
  const noise = ac.createBufferSource(); noise.buffer = getRollBuf(ac);
  const noiseAt = Math.random() * 1.5;
  const bpf   = ac.createBiquadFilter(); bpf.type = 'bandpass';
  const env   = ac.createGain();
  const pan   = ac.createStereoPanner(); pan.pan.value = pos.pan;
  noise.connect(bpf); bpf.connect(env); env.connect(pan); pan.connect(ac.destination);
  releaseAfter(ac, pan, t + 0.15);

  if (surface === 'ground') {
    bpf.frequency.value = 160; bpf.Q.value = 2.0;
    env.gain.setValueAtTime(vol * 0.22, t);
    env.gain.exponentialRampToValueAtTime(0.001, t + 0.10);
    noise.start(t, noiseAt); noise.stop(t + 0.10);
    // Low sine thump
    const th = ac.createOscillator(), tg = ac.createGain();
    th.frequency.setValueAtTime(90, t);
    th.frequency.exponentialRampToValueAtTime(36, t + 0.09);
    tg.gain.setValueAtTime(vol * 0.22, t);
    tg.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
    th.connect(tg); tg.connect(pan); th.start(t); th.stop(t + 0.11);

  } else if (surface === 'box') {
    bpf.frequency.value = 460; bpf.Q.value = 4.0;
    env.gain.setValueAtTime(vol * 0.50, t);
    env.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
    noise.start(t, noiseAt); noise.stop(t + 0.07);
    // Pitched wooden knock
    const kn = ac.createOscillator(), kg = ac.createGain();
    kn.frequency.setValueAtTime(320, t);
    kn.frequency.exponentialRampToValueAtTime(110, t + 0.06);
    kg.gain.setValueAtTime(vol * 0.45, t);
    kg.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    kn.connect(kg); kg.connect(pan); kn.start(t); kn.stop(t + 0.08);

  } else {
    // A bird.
    bpf.frequency.value = 560; bpf.Q.value = 1.8;
    env.gain.setValueAtTime(vol * 0.38, t);
    env.gain.exponentialRampToValueAtTime(0.001, t + 0.13);
    noise.start(t, noiseAt); noise.stop(t + 0.13);
    // Brief squawk chirp
    const ch = ac.createOscillator(), cg = ac.createGain();
    ch.type = 'sawtooth';
    ch.frequency.setValueAtTime(380 + speed * 14, t);
    ch.frequency.exponentialRampToValueAtTime(170, t + 0.09);
    cg.gain.setValueAtTime(vol * 0.22, t);
    cg.gain.exponentialRampToValueAtTime(0.001, t + 0.10);
    ch.connect(cg); cg.connect(pan); ch.start(t); ch.stop(t + 0.10);
  }
}

// At most this many balls carry a rolling-noise loop at once. Each loop is a
// live noise source plus a filter on the audio thread, and a burst of shots
// can put dozens of balls on the ground; past a handful they are inaudible
// anyway. The first to the ground keep their loops; the rest roll silently.
const MAX_ROLL_LOOPS: number = 8;
let rollLoopCount: number = 0;

// A ball's rolling-noise loop, with the last targets sent to its parameters.
interface RollAudio {
  src: AudioBufferSourceNode;
  filt: BiquadFilterNode;
  gain: GainNode;
  pan: StereoPannerNode;
  surface: RollSurface;
  lastVol: number;
  lastPan: number;
  lastFreq: number;
}

interface Ball {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;                 // ticks since it was fired
  grounded: boolean;           // rolling along the terrain or a crate top
  onBox?: Box | null;          // the crate it is rolling along, if any
  resting?: boolean;           // come to rest: physics over, still drawn
  restFrames?: number;         // consecutive slow ticks so far, towards REST_FRAMES
  rollAudio?: RollAudio | null;
  kills?: number;              // gulls this one ball has killed
}

function startBallRoll(b: Ball, surface: RollSurface): void {
  stopBallRoll(b);
  if (rollLoopCount >= MAX_ROLL_LOOPS) return;
  rollLoopCount++;
  const ac  = getAudioCtx();
  const src = ac.createBufferSource();
  src.buffer = getRollBuf(ac); src.loop = true;
  const filt = ac.createBiquadFilter();
  filt.type = 'bandpass';
  filt.frequency.value = surface === 'box' ? 260 : 100;
  filt.Q.value         = surface === 'box' ? 3.5  : 2.2;
  const gain = ac.createGain(); gain.gain.value = 0;
  const pan  = ac.createStereoPanner(); pan.pan.value = 0;
  src.connect(filt); filt.connect(gain); gain.connect(pan); pan.connect(ac.destination);
  src.start();
  // last* remember what was sent so unchanged targets are not resent every
  // frame; every call adds an event to the parameter's timeline.
  b.rollAudio = { src, filt, gain, pan, surface, lastVol: -1, lastPan: 2, lastFreq: -1 };
}

// Send a new target only when it differs enough from the last one sent.
function setTargetIfChanged<K extends string>(
  param: AudioParam, audio: Record<K, number>, key: K, value: number, t: number, tc: number, eps: number,
): void {
  if (Math.abs(audio[key] - value) < eps) return;
  audio[key] = value;
  param.setTargetAtTime(value, t, tc);
}

function tickBallRoll(b: Ball, speed: number): void {
  const a = b.rollAudio;
  if (!a) return;
  const ac = getAudioCtx();
  const t  = ac.currentTime;
  if (speed < 0.35) {
    setTargetIfChanged(a.gain.gain, a, 'lastVol', 0, t, 0.06, 0.001);
    return;
  }
  const pos  = ballAudioPos(b);
  const norm = Math.min(speed / 7, 1);
  const vol  = pos.vol * norm * (a.surface === 'box' ? 0.128 : 0.096);
  const fBase = a.surface === 'box' ? 180 : 70;
  setTargetIfChanged(a.gain.gain,       a, 'lastVol',  vol,                 t, 0.06, 0.002);
  setTargetIfChanged(a.pan.pan,         a, 'lastPan',  pos.pan,             t, 0.06, 0.01);
  setTargetIfChanged(a.filt.frequency,  a, 'lastFreq', fBase + norm * 280,  t, 0.06, 2);
}

function stopBallRoll(b: Ball): void {
  if (!b.rollAudio) return;
  const ac = getAudioCtx();
  const { gain, src, pan } = b.rollAudio;
  b.rollAudio = null;
  rollLoopCount = Math.max(0, rollLoopCount - 1);
  gain.gain.setTargetAtTime(0, ac.currentTime, 0.10);
  src.stop(ac.currentTime + 0.5);
  releaseAfter(ac, pan, ac.currentTime + 0.5);
}

// Arcadish downward-sweep death sound for gulls.
function playGullDeath(g: Gull): void {
  const ac  = getAudioCtx();
  const pos = ballAudioPos({ x: g.x, y: g.y });
  const t   = ac.currentTime;
  const pan = ac.createStereoPanner();
  pan.pan.value = pos.pan;
  pan.connect(ac.destination);
  releaseAfter(ac, pan, t + 0.44);

  const gain = ac.createGain();
  gain.gain.setValueAtTime(pos.vol * 0.55, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.44);
  gain.connect(pan);

  // Sawtooth sweep 720 → 62 Hz (main voice)
  const osc1 = ac.createOscillator();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(720, t);
  osc1.frequency.exponentialRampToValueAtTime(62, t + 0.42);
  osc1.connect(gain); osc1.start(t); osc1.stop(t + 0.44);

  // Square sub an octave below for body
  const osc2 = ac.createOscillator();
  osc2.type = 'square';
  osc2.frequency.setValueAtTime(360, t);
  osc2.frequency.exponentialRampToValueAtTime(31, t + 0.42);
  const subGain = ac.createGain(); subGain.gain.value = 0.35;
  osc2.connect(subGain); subGain.connect(gain);
  osc2.start(t); osc2.stop(t + 0.44);
}

// Two birds with one stone: a cash-register "ka-ching", a sharp mechanical
// tick followed by a bell. The bell rises a whole tone for every bird past
// the second, so a stone's third and fourth kills chain upward.
function playMultiKillJingle(count: number): void {
  const ac = getAudioCtx();
  const t  = ac.currentTime;
  const rise = Math.pow(2, ((count - 2) * 2) / 12);
  const out = ac.createGain();
  out.gain.value = 0.5;
  out.connect(ac.destination);
  releaseAfter(ac, out, t + 0.45);
  // Tick: a brief burst of high noise, the drawer's latch.
  noiseBurst(ac, out, t, 0.03, 3000, 3, 0.35, 0.003);
  // Bell: a sine with a faint upper partial for the ring.
  tone(ac, out, t + 0.03, 0.4,  1319 * rise, 1319 * rise, 0.28);
  tone(ac, out, t + 0.03, 0.25, 1319 * 2.5 * rise, 1319 * 2.5 * rise, 0.08);
}

// ─── Player movement sounds ───────────────────────────────────────────────────
// Footsteps, jumps, and landings, each in a grass and a box flavour. Grass is
// soft: filtered noise with a little low thump. Boxes are hollow wood: a
// pitched knock with a short resonance. All are quiet enough to sit under
// the slingshot and gull sounds. Noise comes from the shared roll buffer at
// a random offset, so nothing is allocated per step.

// A short burst of bandpassed noise into `out`, starting at t.
function noiseBurst(
  ac: AudioContext, out: AudioNode, t: number, dur: number, freq: number, q: number, peak: number, attack = 0.004,
): void {
  const src = ac.createBufferSource();
  src.buffer = getRollBuf(ac);
  const bpf = ac.createBiquadFilter();
  bpf.type = 'bandpass'; bpf.frequency.value = freq; bpf.Q.value = q;
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(bpf); bpf.connect(g); g.connect(out);
  src.start(t, Math.random() * 1.5, dur + 0.02);
  releaseAfter(ac, g, t + dur + 0.02);
}

// A short pitched tone into `out`: sine/triangle body with a frequency glide.
function tone(
  ac: AudioContext, out: AudioNode, t: number, dur: number, f0: number, f1: number, peak: number,
  type: OscillatorType = 'sine', attack = 0.003,
): void {
  const osc = ac.createOscillator(), g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  osc.connect(g); g.connect(out);
  osc.start(t); osc.stop(t + dur + 0.02);
  releaseAfter(ac, g, t + dur + 0.02);
}

// Slight random detune so repeated steps do not sound machine-made.
function jitter(amount: number): number { return 1 + (Math.random() * 2 - 1) * amount; }

// pace: 0..1, how fast the player is moving.
function playFootstep(surface: GroundSurface, pace: number): void {
  const ac  = getAudioCtx();
  const t   = ac.currentTime;
  const vol = 0.5 * (0.55 + 0.45 * pace);   // footsteps sit well under everything else
  if (surface === 'box') {
    // Hollow wooden knock: a bandpassed tick with a resonant low body.
    noiseBurst(ac, ac.destination, t, 0.05, 520 * jitter(0.08), 4, 0.16 * vol);
    tone(ac, ac.destination, t, 0.075, 230 * jitter(0.06), 150, 0.13 * vol, 'triangle');
  } else {
    // Grass: a soft crunch, mostly noise, with a faint thump underneath.
    noiseBurst(ac, ac.destination, t, 0.07, 1200 * jitter(0.12), 0.9, 0.055 * vol);
    noiseBurst(ac, ac.destination, t + 0.012, 0.045, 3000 * jitter(0.1), 1.2, 0.025 * vol);
    tone(ac, ac.destination, t, 0.05, 95 * jitter(0.05), 60, 0.04 * vol);
  }
}

function playJump(surface: GroundSurface): void {
  // Not a cartoon boing: a heavy double step as the player pushes off, then
  // a quiet exhale. The steps use the surface's material, lower and
  // weightier than a walking step, the second harder. They are packed into
  // the first few hundredths of a second so both land while the feet are
  // still on the ground, not while the player is already in the air.
  const ac = getAudioCtx();
  const t  = ac.currentTime;
  const steps: [delay: number, weight: number][] = [[0, 0.8], [0.035, 1.0]];
  for (const [dt, w] of steps) {
    if (surface === 'box') {
      noiseBurst(ac, ac.destination, t + dt, 0.04, 400 * jitter(0.05), 3.5, 0.18 * w, 0.002);
      tone(ac, ac.destination, t + dt, 0.05, 175 * jitter(0.04), 105, 0.17 * w, 'triangle', 0.002);
    } else {
      noiseBurst(ac, ac.destination, t + dt, 0.045, 850 * jitter(0.1), 0.9, 0.11 * w, 0.002);
      tone(ac, ac.destination, t + dt, 0.045, 80 * jitter(0.05), 45, 0.15 * w, 'sine', 0.002);
    }
  }
  // Exhale: breathy noise with a soft attack, drifting down as it fades.
  const ex  = ac.createBufferSource();
  ex.buffer = getRollBuf(ac);
  const bpf = ac.createBiquadFilter();
  bpf.type = 'bandpass'; bpf.Q.value = 0.7;
  const exAt = t + 0.07;
  bpf.frequency.setValueAtTime(900, exAt);
  bpf.frequency.exponentialRampToValueAtTime(450, exAt + 0.25);
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, exAt);
  g.gain.linearRampToValueAtTime(0.035, exAt + 0.05);
  g.gain.exponentialRampToValueAtTime(0.001, exAt + 0.27);
  ex.connect(bpf); bpf.connect(g); g.connect(ac.destination);
  ex.start(exAt, Math.random() * 1.5, 0.3);
  releaseAfter(ac, g, exAt + 0.27);
}

// impact: the downward speed at touchdown, px per tick.
function playLand(surface: GroundSurface, impact: number): void {
  const ac  = getAudioCtx();
  const t   = ac.currentTime;
  const vol = Math.min(1, 0.35 + impact / 14);
  if (surface === 'box') {
    // Landing on a crate: a hollow thud with a woody resonance and a click.
    noiseBurst(ac, ac.destination, t, 0.09, 380, 3, 0.26 * vol);
    tone(ac, ac.destination, t, 0.12, 190, 115, 0.22 * vol, 'triangle');
    noiseBurst(ac, ac.destination, t, 0.015, 2400, 2, 0.08 * vol, 0.002);
  } else {
    // Landing on grass: a dull thump and a rustle.
    tone(ac, ac.destination, t, 0.13, 75, 40, 0.30 * vol);
    noiseBurst(ac, ac.destination, t, 0.10, 700, 0.8, 0.14 * vol);
    noiseBurst(ac, ac.destination, t + 0.03, 0.08, 1800, 1, 0.06 * vol);
  }
}

// The elastic letting go: a short breathy sweep downward as the ball leaves the
// cradle, with a faint twang underneath for the sling itself. Both scale with
// how far it was drawn, so a nudge is barely audible and a full pull is a proper
// whoosh. Kept quiet: the shot it fires will be making its own noise shortly.
function playSlingshotRelease(pull: number): void {
  const ac = getAudioCtx();
  const t  = ac.currentTime;
  const strength = Math.min(1, Math.max(0, pull));
  const dur = 0.10 + strength * 0.08;

  // Air past the ball: broad noise whose band falls as the shot leaves.
  const src = ac.createBufferSource();
  src.buffer = getRollBuf(ac);
  const bpf = ac.createBiquadFilter();
  bpf.type = 'bandpass';
  bpf.Q.value = 1.1;
  bpf.frequency.setValueAtTime(700 + strength * 1500, t);
  bpf.frequency.exponentialRampToValueAtTime(320, t + dur);
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(0.05 + strength * 0.11, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(bpf); bpf.connect(g); g.connect(ac.destination);
  src.start(t, Math.random() * 1.5, dur + 0.02);
  releaseAfter(ac, g, t + dur + 0.02);

  // The band snapping back. A tone carries much further than the same peak of
  // broadband noise, so this sits well below the whoosh: loud enough to give the
  // release a body, quiet enough that the air is still what you hear.
  tone(ac, ac.destination, t, 0.05 + strength * 0.03,
       260 + strength * 120, 90, 0.012 + strength * 0.022, 'triangle');
}

// Sharp latex pop + brief arcade down-sweep.
function playBalloonPop(wx: number, wy: number): void {
  const ac  = getAudioCtx();
  const pos = ballAudioPos({ x: wx, y: wy });
  const t   = ac.currentTime;

  const pan = ac.createStereoPanner();
  pan.pan.value = pos.pan;
  pan.connect(ac.destination);
  releaseAfter(ac, pan, t + 0.20);

  // ── Snap: short white-noise burst shaped like a gunshot ──────────────────
  const snapGain = ac.createGain();
  snapGain.gain.setValueAtTime(pos.vol * 1.4, t);
  snapGain.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
  snapGain.connect(pan);

  const snap = ac.createBufferSource();
  snap.buffer = getRollBuf(ac);

  // High-pass to make it crisp
  const hp = ac.createBiquadFilter();
  hp.type = 'highpass'; hp.frequency.value = 1800;
  snap.connect(hp); hp.connect(snapGain);
  snap.start(t, Math.random() * 1.5); snap.stop(t + 0.07);

  // ── Arcade sweep: sawtooth 900 → 120 Hz, 0.18 s ─────────────────────────
  const sweepGain = ac.createGain();
  sweepGain.gain.setValueAtTime(pos.vol * 0.5, t + 0.02);
  sweepGain.gain.exponentialRampToValueAtTime(0.001, t + 0.20);
  sweepGain.connect(pan);

  const osc = ac.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(900, t + 0.02);
  osc.frequency.exponentialRampToValueAtTime(120, t + 0.20);
  osc.connect(sweepGain);
  osc.start(t + 0.02); osc.stop(t + 0.20);
}

function getWind(): number {
  return Math.sin(windTick * 0.0008) * 1.2
       + Math.sin(windTick * 0.0021) * 0.6
       + windNoise;
}

// ─── Input ────────────────────────────────────────────────────────────────────
const keys: Record<string, boolean> = {};   // by KeyboardEvent.code; true while held
const mouse: Vec = { x: 0, y: 0 };

// Whether a key is down. A code that was never pressed has no entry at all.
function held(code: string): boolean {
  return keys[code] === true;
}
window.addEventListener('mousemove', e => {
  const p = toView(e.clientX, e.clientY);
  mouse.x = p.x;
  mouse.y = p.y;
});

// A jump only leaves the ground, and only once per press; both the keyboard
// and the touch pad go through here.
function tryJump(): void {
  if (!onGround) return;
  player.vy += JUMP_FORCE;
  onGround = false;
  playJump(groundSurface);
}

window.addEventListener('keydown', e => {
  // A key press is a user gesture: a good moment to make sure audio can run.
  const ac = getAudioCtx();
  if (ac.state === 'suspended') void ac.resume();   // nothing to do when it has

  if (!gameStarted || paused) return;   // nothing pressed under the Paused box carries into the resume
  if (!held(e.code) && (e.code === 'ArrowUp' || e.code === 'KeyW' || e.code === 'Space')) tryJump();
  keys[e.code] = true;
});
window.addEventListener('keyup', e => { keys[e.code] = false; });

window.addEventListener('contextmenu', e => { e.preventDefault(); });
window.addEventListener('mousedown', e => {
  if (e.button !== 0) return;
  if (!gameStarted) return;               // the landing page handles this click
  if (paused) { resumeGame(); return; }   // the resuming click does not fire the slingshot
  mouseDown = true;
  slingshotPull = 1;                        // every shot is a full-power shot: no charging
});

// Lets go of everything the player is holding: keys, the mouse button, the
// slingshot and every finger's claim on the pad or the aim. Used when a match
// pauses, ends or restarts, so nothing carries over into what comes next.
function releaseInputs(): void {
  for (const k of Object.keys(keys)) keys[k] = false;
  mouseDown = false;
  slingshotPull = 0;
  heldPadButtons.clear();
  padHeld.clear();
  touchJobs.clear();
  aimTouchId = null;
}

// Losing focus pauses the game: a switch to another window should never cost
// the player. Everything held is released so nothing carries over — a thumb
// still on the pad is treated as lifted and has to land again — and the
// continuous sounds stop; grounded balls start rolling again on resume. The box
// drawn by drawPauseScreen says click (or tap) to resume.
function pauseGame(): void {
  if (paused || gameWon || !gameStarted) return;
  paused = true;
  matchElapsedMs += performance.now() - matchStartMs;   // the clock stops with the game
  releaseInputs();
  fadeOutSkiAudio();
  for (const b of balls) stopBallRoll(b);
}
function resumeGame(): void {
  paused = false;
  matchStartMs = performance.now();
  accumulator = 0;   // drop the time that passed while paused
}
window.addEventListener('blur', pauseGame);
// Letting go. Shared by the mouse and by a finger lifting off. A shot is
// always at full power, so a brief tap fires just like a long hold; the aim is
// re-solved here so a tap that never saw a frame still flies the right arc.
function releaseSlingshot(): void {
  mouseDown = false;
  if (slingshotPull > 0.05) {
    slingshotPull = 1;
    solvedAimAngle = computeSolvedAimAngle(1);
    playSlingshotRelease(slingshotPull);
    const angle = solvedAimAngle;
    const at    = launchPoint(angle, slingshotPull);
    balls.push({
      x:        at.x,
      y:        at.y,
      vx:       Math.cos(angle) * BALL_SPEED * slingshotPull + player.vx,
      vy:       Math.sin(angle) * BALL_SPEED * slingshotPull + player.vy,
      age:      0,
      grounded: false,
    });
  }
  slingshotPull = 0;
}

window.addEventListener('mouseup', e => {
  if (e.button !== 0 || !mouseDown) return;
  releaseSlingshot();
});

// Opening the start screen, whether by click or by tap.
function pressStart(): void {
  const ac = getAudioCtx();
  if (ac.state === 'suspended') void ac.resume();   // nothing to do when it has

  if (gameWon) resetMatch();   // coming back from a finished run: fresh world
  startMatch();
}

// ─── Touch controls ───────────────────────────────────────────────────────────
// A touch device gets a movement pad in the bottom-left corner and treats the
// rest of the screen as the slingshot: tap to fire, or hold and drag to aim
// first. One thumb moves, the other shoots.
//
// The pad is laid out in canvas pixels rather than viewport ones. A thumb is a
// fixed physical size, while the viewport is scaled to fit whatever window it
// is given — on an 844x390 phone that scale is about 0.36, so a pad sized in
// viewport units would come out about a third of the size it needs to be.

// Set from the pointer the device reports, and again the moment a finger
// actually lands: a laptop with a touchscreen keeps its keyboard hints until
// someone uses the glass.
let touchMode: boolean = false;
try {
  touchMode = window.matchMedia('(pointer: coarse)').matches;
} catch {
  // No matchMedia here; assume a mouse until a touch says otherwise.
}

const PAD_MIN_BUTTON: number = 52;    // CSS px: never smaller than a comfortable thumb target
const PAD_MAX_BUTTON: number = 108;   // CSS px
const PAD_BUTTON_FRAC: number = 0.20; // of the canvas's short side, so it scales with the device
const PAD_DEAD_FRAC: number = 0.35;   // the neutral middle, as a fraction of one button
const PAD_REACH_FRAC: number = 2.0;   // how far a committed thumb may stray, likewise

type PadButtonId = 'left' | 'right' | 'jump' | 'slide';

// The key each pad button stands in for.
const PAD_KEYS: Record<PadButtonId, string> = {
  left:  'ArrowLeft',
  right: 'ArrowRight',
  jump:  'ArrowUp',
  slide: 'ArrowDown',
};

interface PadButton extends Rect { id: PadButtonId; key: string }

interface PadGeometry {
  size: number;          // one button's side, in canvas pixels
  cx: number;            // where the two arms cross
  cy: number;
  buttons: PadButton[];
}

// The four buttons as canvas-pixel rectangles: left and right either side of a
// crease, jump above it and slide below, making a cross. The rectangles are
// where the lit limbs are drawn; which directions a thumb holds is decided by
// its angle around the centre (padDirectionsAt), so there is no seam between
// them for a thumb to fall through. The pad stands off the left and bottom
// edges by its margin plus the safe-area inset there.
function padGeometry(): PadGeometry {
  const short = Math.min(canvas.width, canvas.height);
  const size = Math.max(PAD_MIN_BUTTON * dpr, Math.min(PAD_MAX_BUTTON * dpr, short * PAD_BUTTON_FRAC));
  const margin = size * 0.40;
  const left = margin + safeInsets.left * dpr;
  const slideY = canvas.height - safeInsets.bottom * dpr - margin - size;
  const midY = slideY - size;
  return {
    size,
    cx: left + 1.5 * size,        // where the two arms cross
    cy: midY + 0.5 * size,
    buttons: [
      { id: 'left',  key: PAD_KEYS.left,  x: left,            y: midY,       w: size, h: size },
      { id: 'right', key: PAD_KEYS.right, x: left + 2 * size, y: midY,       w: size, h: size },
      { id: 'jump',  key: PAD_KEYS.jump,  x: left + size,     y: midY - size, w: size, h: size },
      { id: 'slide', key: PAD_KEYS.slide, x: left + size,     y: slideY,     w: size, h: size },
    ],
  };
}

const heldPadButtons: Set<PadButtonId> = new Set<PadButtonId>();               // ids currently under some finger
const padHeld: Map<number, Set<PadButtonId>> = new Map<number, Set<PadButtonId>>();         // touch identifier -> the ids that finger holds
type TouchJob = 'pad' | 'aim';
const touchJobs: Map<number, TouchJob> = new Map<number, TouchJob>();               // touch identifier -> what that finger is doing
let aimTouchId: number | null = null;                        // the one finger drawing the slingshot

// Where a finger has to land to take the pad rather than the slingshot: the
// square the cross sits in, corners included, so a thumb can start out on a
// diagonal as readily as on an arm.
function hitsPad(clientX: number, clientY: number): boolean {
  const c = toCanvas(clientX, clientY);
  const g = padGeometry();
  return Math.abs(c.x - g.cx) <= 1.5 * g.size
      && Math.abs(c.y - g.cy) <= 1.5 * g.size;
}

// What a finger at this point is asking for. Once it is clear of the middle the
// angle alone decides, in eight sectors of 45 degrees, so the arms have no seam
// between them to fall down: a thumb leaving right for jump crosses a sector
// that holds both, and the player hops the box without breaking stride, then
// picks the walk back up on the way home. Nothing is held in the neutral middle
// or out beyond the pad's reach — but the finger keeps its claim out there, and
// steers again the moment it slides back within reach.
function padDirectionsAt(clientX: number, clientY: number): PadButtonId[] {
  const c = toCanvas(clientX, clientY);
  const g = padGeometry();
  const dx = c.x - g.cx;
  const dy = c.y - g.cy;
  const away = Math.hypot(dx, dy);
  if (away < g.size * PAD_DEAD_FRAC || away > g.size * PAD_REACH_FRAC) return [];
  const sector = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) & 7;
  const sectors: PadButtonId[][] = [
    ['right'], ['right', 'slide'], ['slide'], ['left', 'slide'],
    ['left'],  ['left', 'jump'],   ['jump'],  ['right', 'jump'],
  ];
  return at(sectors, sector);
}

// Points one finger at a new set of directions. A key only goes down if it was
// not already, and only comes up once no other finger is still asking for it,
// so two thumbs on the pad cannot cut each other off.
function setPadDirections(identifier: number, wanted: PadButtonId[]): void {
  const had = padHeld.get(identifier) ?? new Set<PadButtonId>();
  const now = new Set(wanted);
  padHeld.set(identifier, now);

  for (const id of had) {
    if (now.has(id)) continue;
    let elsewhere: boolean = false;
    for (const ids of padHeld.values()) if (ids.has(id)) elsewhere = true;
    if (elsewhere) continue;
    heldPadButtons.delete(id);
    keys[PAD_KEYS[id]] = false;
  }
  for (const id of now) {
    if (heldPadButtons.has(id)) continue;
    heldPadButtons.add(id);
    keys[PAD_KEYS[id]] = true;
    if (id === 'jump') tryJump();
  }
}

function aimAt(clientX: number, clientY: number): void {
  const p = toView(clientX, clientY);
  mouse.x = p.x;
  mouse.y = p.y;
}

// Lets go of whatever a lifted or cancelled finger was doing.
function endTouchJob(identifier: number): void {
  const job = touchJobs.get(identifier);
  if (job === undefined) return;
  touchJobs.delete(identifier);
  if (job === 'aim') {
    aimTouchId = null;
    if (mouseDown) releaseSlingshot();
  } else {
    setPadDirections(identifier, []);
    padHeld.delete(identifier);
  }
}

window.addEventListener('touchstart', e => {
  touchMode = true;
  const ac = getAudioCtx();
  if (ac.state === 'suspended') void ac.resume();   // nothing to do when it has

  for (const t of e.changedTouches) {
    if (!gameStarted) {
      const p = toUI(t.clientX, t.clientY);
      if (hitsRect(startButtonRect(), p.x, p.y)) pressStart();
      continue;
    }
    if (paused) { resumeGame(); continue; }

    if (hitsPad(t.clientX, t.clientY)) {
      touchJobs.set(t.identifier, 'pad');
      setPadDirections(t.identifier, padDirectionsAt(t.clientX, t.clientY));
    } else if (aimTouchId === null) {
      // Anywhere off the pad draws the slingshot. Only the first such finger
      // does; a second one would fight it for the aim.
      aimTouchId = t.identifier;
      touchJobs.set(t.identifier, 'aim');
      aimAt(t.clientX, t.clientY);
      mouseDown = true;
      slingshotPull = 1;                  // full power immediately, as with the mouse
    }
  }
  e.preventDefault();
}, { passive: false });

window.addEventListener('touchmove', e => {
  for (const t of e.changedTouches) {
    const job = touchJobs.get(t.identifier);
    if (job === undefined) continue;
    if (job === 'aim') aimAt(t.clientX, t.clientY);
    else setPadDirections(t.identifier, padDirectionsAt(t.clientX, t.clientY));
  }
  e.preventDefault();
}, { passive: false });

for (const ending of ['touchend', 'touchcancel'] as const) {
  window.addEventListener(ending, e => {
    for (const t of e.changedTouches) endTouchJob(t.identifier);
    e.preventDefault();
  }, { passive: false });
}

// The outline of the pad: twelve corners, the eight around the tips bulging out
// and the four in the armpits curving back in, traced as one path so the cross
// fills and strokes as a single piece of plastic rather than four loose keys.
function tracePadCross(g: PadGeometry): void {
  const a = g.size * 0.5;                     // half a limb's width
  const b = g.size * 1.5;                     // the middle out to a tip
  const corners: Point[] = [
    [-a, -b], [a, -b], [a, -a], [b, -a], [b, a], [a, a],
    [a, b], [-a, b], [-a, a], [-b, a], [-b, -a], [-a, -a],
  ];
  const v: Point[] = corners.map(([x, y]): Point => [g.cx + x, g.cy + y]);
  const armpits = new Set([2, 5, 8, 11]);     // the corners that turn inward
  const mid = (p: Point, q: Point): Point => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];

  ctx.beginPath();
  ctx.moveTo(...mid(at(v, v.length - 1), at(v, 0)));
  for (const [i, corner] of v.entries()) {
    const next = at(v, (i + 1) % v.length);
    // Fat and bubbly at the tips, a gentler tuck where the limbs meet.
    ctx.arcTo(...corner, ...mid(corner, next), g.size * (armpits.has(i) ? 0.22 : 0.30));
  }
  ctx.closePath();
}

// Drawn in canvas pixels, outside the letterboxed viewport, so the pad keeps a
// thumb's size whatever the window is doing. The limb under the thumb lights up
// — two of them once the thumb is out on a diagonal, which is what running and
// jumping at the same time looks like.
function drawTouchPad(): void {
  const g = padGeometry();
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  tracePadCross(g);
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  ctx.fill();

  // Lit limbs are clipped to the cross so they stop at its edge instead of
  // squaring off the corners it just rounded.
  ctx.save();
  ctx.clip();
  ctx.fillStyle = 'rgba(255,255,255,0.26)';
  for (const b of g.buttons) {
    if (!heldPadButtons.has(b.id)) continue;
    ctx.beginPath();
    ctx.roundRect(b.x, b.y, b.w, b.h, b.w * 0.30);
    ctx.fill();
  }
  ctx.restore();

  tracePadCross(g);
  ctx.strokeStyle = 'rgba(255,255,255,0.38)';
  ctx.lineWidth = 2 * dpr;
  ctx.stroke();

  // A soft square hub over the crossing: the thumb's home, and the one spot on
  // the pad that asks for nothing.
  const hub = g.size * 0.52;
  ctx.beginPath();
  ctx.roundRect(g.cx - hub / 2, g.cy - hub / 2, hub, hub, hub * 0.38);
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fill();

  // An arrowhead pointing the way each limb goes, stroked as well as filled so
  // its corners come out rounded off.
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const arrows: Record<PadButtonId, Point> = { left: [-1, 0], right: [1, 0], jump: [0, -1], slide: [0, 1] };
  for (const b of g.buttons) {
    const held = heldPadButtons.has(b.id);
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2, r = b.w * 0.20;
    const [dx, dy] = arrows[b.id];
    ctx.beginPath();
    ctx.moveTo(cx + dx * r, cy + dy * r);
    ctx.lineTo(cx - dx * r + dy * r, cy - dy * r + dx * r);
    ctx.lineTo(cx - dx * r - dy * r, cy - dy * r - dx * r);
    ctx.closePath();
    ctx.fillStyle = ctx.strokeStyle = held ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.60)';
    ctx.lineWidth = r * 0.45;
    ctx.stroke();
    ctx.fill();
  }
  ctx.restore();
}

// ─── Player ───────────────────────────────────────────────────────────────────
interface Player extends Vec { vx: number; vy: number; radius: number }
const player: Player = { x: 0, y: 0, vx: 0, vy: 0, radius: PLAYER_RADIUS };

// Init terrain first so terrainY(0) is valid
initTerrain();
player.y = terrainY(0) - player.radius;
centreCamera();

// ─── Drawing ──────────────────────────────────────────────────────────────────

// The shading on a ball: a highlight up and to the left, grey at the rim. Every
// ball wears the same one, so it is built once in ball-local coordinates and the
// canvas is moved under it. Building a gradient is among the more expensive
// things a 2D canvas is asked to do, and this was one per ball per frame.
let ballGradient: CanvasGradient | null = null;
function getBallGradient(): CanvasGradient {
  if (!ballGradient) {
    ballGradient = ctx.createRadialGradient(-2, -2, 0.5, 0, 0, BALL_RADIUS);
    ballGradient.addColorStop(0,    '#ffffff');
    ballGradient.addColorStop(0.35, '#d4d4d4');
    ballGradient.addColorStop(1,    '#888888');
  }
  return ballGradient;
}
function drawPalmTree(tree: Tree, baseY: number): void {
  const topX = tree.x + tree.lean;
  const topY = baseY - tree.height;

  ctx.beginPath();
  ctx.moveTo(tree.x, baseY);
  ctx.quadraticCurveTo(tree.x + tree.lean * 0.5, baseY - tree.height * 0.5, topX, topY);
  ctx.lineWidth = 8;
  ctx.strokeStyle = '#7a5230';
  ctx.lineCap = 'round';
  ctx.stroke();

  const fronds = [
    { cx: -25, cy: -20, ex: -65, ey:  -5 },
    { cx: -15, cy: -30, ex: -40, ey: -50 },
    { cx:   0, cy: -35, ex:   5, ey: -60 },
    { cx:  15, cy: -30, ex:  40, ey: -50 },
    { cx:  25, cy: -20, ex:  65, ey:  -5 },
    { cx:  18, cy:  -8, ex:  48, ey:  18 },
    { cx: -18, cy:  -8, ex: -48, ey:  18 },
  ];
  ctx.lineWidth = 5;
  ctx.strokeStyle = '#2a7a2a';
  for (const f of fronds) {
    ctx.beginPath();
    ctx.moveTo(topX, topY);
    ctx.quadraticCurveTo(topX + f.cx, topY + f.cy, topX + f.ex, topY + f.ey);
    ctx.stroke();
  }

  if (tree.balloon) {
    const b = tree.balloon;
    const bx = topX + b.dx;
    const by = topY + b.dy;
    ctx.beginPath(); ctx.moveTo(topX, topY); ctx.lineTo(bx, by + BALLOON_RADIUS);
    ctx.strokeStyle = '#aaa'; ctx.lineWidth = 1; ctx.stroke();
    ctx.beginPath(); ctx.arc(bx, by, BALLOON_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = '#e02020'; ctx.fill();
    ctx.beginPath(); ctx.arc(bx - 3, by - 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.fill();
    ctx.beginPath(); ctx.arc(bx, by + BALLOON_RADIUS, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#a01010'; ctx.fill();
  }
}

// ─── Balloon physics ──────────────────────────────────────────────────────────

// A tree's balloon: buoyancy, drag and the wind, held by its string.
function stepBalloonPhysics(b: TreeBalloon, wind: number): void {
  b.vy -= BALLOON_BUOYANCY;
  b.vx += wind * 0.012;
  b.vx *= BALLOON_DRAG;
  b.vy *= BALLOON_DRAG;
  b.dx += b.vx;
  b.dy += b.vy;
  const dist = Math.sqrt(b.dx * b.dx + b.dy * b.dy);
  const strLen = b.stringLen ?? BALLOON_STRING_LEN;
  if (dist > strLen) {
    const nx = b.dx / dist, ny = b.dy / dist;
    b.dx = nx * strLen;
    b.dy = ny * strLen;
    const dot = b.vx * nx + b.vy * ny;
    b.vx -= dot * nx; b.vy -= dot * ny;
  }
}

const ROPE_SEGS: number      = 10;
const ROPE_DAMP: number      = 0.934;  // string segment damping — 3× friction (was 0.978)
const SKI_DRAG: number       = 0.988;  // multiplicative friction per tick in ski mode (vs additive FRICTION=1.2)
const BALLOON_DAMP: number   = 0.05;   // balloon tip damping — 3× friction, clamped stable (was 0.60)
const ROPE_GRAV: number      = 0.008;  // very light string — almost no sag
const ROPE_BUOY: number      = 4.92;   // upward buoyancy — 3× (was 1.64)
const ROPE_ITERS: number     = 15;     // constraint solver iterations

const BALL_RADIUS: number      = 5;
const BALL_SPEED: number       = 33.88;  // px/tick at full draw (44 × 0.70 × 1.10)
const BALL_DRAG: number        = 0.995;
const BALL_RESTITUTION: number = 0.32;   // terrain bounce restitution
const BALL_BOX_BOUNCE: number  = 0.50;   // box bounce restitution
const BALL_ROLL_DRAG: number   = 0.990;  // rolling friction per tick

const RAGDOLL_DAMP: number     = 0.970;  // per-tick velocity retention (internal friction)
const RAGDOLL_ITERS: number    = 10;     // constraint-solver passes per tick

// A balloon the player holds: a Verlet string of nodes from the player (node 0)
// to the balloon at its tip, each remembering where it was last tick.
interface RopeNode extends Vec { px: number; py: number }
interface RopeBalloon { nodes: RopeNode[]; segLen: number }

// The balloon itself: the last node of the string, ROPE_SEGS from the player.
function tipOf(b: RopeBalloon): RopeNode {
  return at(b.nodes, ROPE_SEGS);
}

function createRopeBalloon(
  anchorX: number, anchorY: number, tipX: number, tipY: number, tipVx: number, tipVy: number, stringLen: number,
): RopeBalloon {
  const segLen = stringLen / ROPE_SEGS;
  const nodes: RopeNode[] = [];
  for (let i: number = 0; i <= ROPE_SEGS; i++) {
    const t  = i / ROPE_SEGS;
    const wx = anchorX + (tipX - anchorX) * t;
    const wy = anchorY + (tipY - anchorY) * t;
    nodes.push({ x: wx, y: wy, px: wx - tipVx * t * 0.3, py: wy - tipVy * t * 0.3 });
  }
  return { nodes, segLen };
}

function updateRopeBalloon(b: RopeBalloon, wind: number): void {
  const nodes = b.nodes;
  const N     = nodes.length;
  const anchor = at(nodes, 0);

  // Pin anchor to player
  anchor.x = anchor.px = player.x;
  anchor.y = anchor.py = player.y;

  // Verlet integrate all free nodes
  for (let i: number = 1; i < N; i++) {
    const n     = at(nodes, i);
    const isTip = i === N - 1;
    const damp  = isTip ? BALLOON_DAMP : ROPE_DAMP;
    const vx    = (n.x - n.px) * damp;
    const vy    = (n.y - n.py) * damp;
    n.px = n.x; n.py = n.y;
    n.x += vx + wind * (isTip ? 0.025 : 0.006);
    n.y += vy + (isTip ? -ROPE_BUOY : ROPE_GRAV);
  }

  // Constraint + collision iterations
  for (let iter: number = 0; iter < ROPE_ITERS; iter++) {
    anchor.x = player.x; anchor.y = player.y;

    // Segment length constraints
    for (let i: number = 0; i < N - 1; i++) {
      const na = at(nodes, i), nb = at(nodes, i + 1);
      const dx = nb.x - na.x, dy = nb.y - na.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 0.001) continue;
      const corr = (dist - b.segLen) / dist;
      if (i === 0) {
        nb.x -= dx * corr; nb.y -= dy * corr;
      } else {
        na.x += dx * corr * 0.5; na.y += dy * corr * 0.5;
        nb.x -= dx * corr * 0.5; nb.y -= dy * corr * 0.5;
      }
    }

    // Collision with player body for all free nodes
    for (let i: number = 1; i < N; i++) {
      const n      = at(nodes, i);
      const minDist = player.radius + (i === N - 1 ? BALLOON_RADIUS : 4);
      const dx = n.x - player.x, dy = n.y - player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist && dist > 0) {
        const push = minDist / dist - 1;
        n.x += dx * push; n.y += dy * push;
      }
    }
  }
}

function updateBalloons(): void {
  const wind = getWind();

  // Tree balloons
  for (const tree of trees) {
    if (!tree.balloon) continue;
    const topX   = tree.x + tree.lean;
    const topY   = terrainY(tree.x) - tree.height;
    const b      = tree.balloon;
    const bWorldX = topX + b.dx, bWorldY = topY + b.dy;
    const dx = player.x - bWorldX, dy = player.y - bWorldY;
    if (dx * dx + dy * dy < (player.radius + BALLOON_RADIUS) ** 2) {
      const stringLen = BALLOON_STRING_LEN * (1 + Math.random() * 0.5);
      playerBalloons.push(createRopeBalloon(
        player.x, player.y, bWorldX, bWorldY, b.vx, b.vy, stringLen
      ));
      tree.balloon = null;
      onBalloonPickup();
      continue;
    }
    stepBalloonPhysics(b, wind);
  }

  // Balloon-balloon tip repulsion (position-based, Verlet-friendly)
  for (let i: number = 0; i < playerBalloons.length; i++) {
    for (let j: number = i + 1; j < playerBalloons.length; j++) {
      const na = tipOf(at(playerBalloons, i));
      const nb = tipOf(at(playerBalloons, j));
      const ddx = na.x - nb.x, ddy = na.y - nb.y;
      const dist = Math.sqrt(ddx * ddx + ddy * ddy);
      const minDist = BALLOON_RADIUS * 2;
      if (dist < minDist && dist > 0) {
        const push = (minDist - dist) * 0.5;
        const nx = ddx / dist, ny = ddy / dist;
        na.x += nx * push; na.y += ny * push;
        nb.x -= nx * push; nb.y -= ny * push;
      }
    }
  }

  // Rope physics for each player balloon
  for (const b of playerBalloons) updateRopeBalloon(b, wind);
}

// ─── Gull ragdoll ─────────────────────────────────────────────────────────────
// Particles (world-space Verlet nodes): P0 body-centre, P1 neck, P2 head,
//   P3 body-back, P4 tail-tip, P5 wing-tip.
// Leg stubs (P6 front-foot, P7 back-foot) are kinematic dangles from P0.

interface RagdollParticle extends Vec { px: number; py: number; mass: number }

// The eight particles, in the order the comment above gives them.
type RagdollParticles = [
  body: RagdollParticle, neck: RagdollParticle, head: RagdollParticle, back: RagdollParticle,
  tail: RagdollParticle, wing: RagdollParticle, frontFoot: RagdollParticle, backFoot: RagdollParticle,
];

// A bone: two particles held exactly `rest` apart.
interface BoneConstraint { i: number; j: number; rest: number }

// A joint limit: two particles that may not come closer than `min`.
interface MinDistanceConstraint { i: number; j: number; min: number }

interface GullRagdoll {
  p: RagdollParticles;
  constraints:    BoneConstraint[];
  minConstraints: MinDistanceConstraint[];
}

function makeGullRagdoll(g: Gull): GullRagdoll {
  const sz = GULL_SZ, df = g.dir;
  const ca = Math.cos(g.angle), sa = Math.sin(g.angle);

  // Convert gull-local coords (lx,ly) to world position, respecting current
  // orientation angle and facing direction.
  const wp  = (lx: number, ly: number): Vec => ({
    x: g.x + lx * df * ca - ly * sa,
    y: g.y + lx * df * sa + ly * ca,
  });
  // Particle factory — inherits rigid-body linear + angular velocity.
  const mkP = (lx: number, ly: number, mass: number): RagdollParticle => {
    const { x, y } = wp(lx, ly);
    const rx = x - g.x, ry = y - g.y;
    return {
      x, y,
      px: x - (g.vx - g.angularVel * ry),   // prev pos encodes velocity
      py: y - (g.vy + g.angularVel * rx),
      mass,
    };
  };

  const p: RagdollParticles = [
    mkP(0,            0,           4.0),  // 0  body centre   (heavy)
    mkP(sz * 0.90,    0,           1.0),  // 1  neck / front
    mkP(sz * 1.35,   -sz * 0.31,  1.5),  // 2  head
    mkP(-sz * 0.95,   0,           2.0),  // 3  body back
    mkP(-sz * 1.60,  -sz * 0.12,  0.4),  // 4  tail tip
    mkP(sz * 0.72,   -sz * 0.85,  0.4),  // 5  upper wing tip
    mkP(sz * 0.25,    sz * 0.84,  0.25), // 6  front foot
    mkP(-sz * 0.18,   sz * 0.84,  0.25), // 7  back foot
  ];

  // Exact-length (bilateral) constraints keep bone lengths rigid.
  const constraints: BoneConstraint[] = [
    { i: 0, j: 1, rest: sz * 0.90  },  // body centre → neck
    { i: 1, j: 2, rest: sz * 0.50  },  // neck → head
    { i: 0, j: 3, rest: sz * 0.95  },  // body centre → body back
    { i: 3, j: 4, rest: sz * 0.70  },  // body back → tail tip
    { i: 0, j: 5, rest: sz * 1.47  },  // body centre → wing tip (wing bone)
    { i: 1, j: 5, rest: sz * 0.92  },  // neck → wing tip (wing triangle brace)
    { i: 0, j: 6, rest: sz * 0.88  },  // body → front foot  (front leg)
    { i: 0, j: 7, rest: sz * 0.88  },  // body → back foot   (back leg)
  ];

  // Minimum-distance (unilateral) constraints prevent hyper-folding at joints.
  const minConstraints: MinDistanceConstraint[] = [
    { i: 1, j: 3, min: sz * 1.65 },  // body stays roughly linear
    { i: 2, j: 3, min: sz * 1.20 },  // head can't fold back to tail end
    { i: 4, j: 1, min: sz * 1.20 },  // tail can't fold forward past neck
    { i: 6, j: 7, min: sz * 0.22 },  // feet don't fully overlap
  ];

  return { p, constraints, minConstraints };
}

function updateGullRagdoll(rdoll: GullRagdoll): void {
  const { p, constraints, minConstraints } = rdoll;

  // Verlet integrate all particles with damping (simulates internal friction).
  for (const pt of p) {
    const vx = (pt.x - pt.px) * RAGDOLL_DAMP;
    const vy = (pt.y - pt.py) * RAGDOLL_DAMP;
    pt.px = pt.x;  pt.py = pt.y;
    pt.x += vx;
    pt.y += vy + GRAVITY;
  }

  for (let iter: number = 0; iter < RAGDOLL_ITERS; iter++) {
    // Bilateral distance constraints (rigid bones).
    for (const c of constraints) {
      const a = at(p, c.i), b = at(p, c.j);
      const dx = b.x - a.x, dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < 1e-6) continue;
      const corr = (d - c.rest) / d;
      const wa = b.mass / (a.mass + b.mass);
      const wb = 1 - wa;
      a.x += dx * corr * wa;  a.y += dy * corr * wa;
      b.x -= dx * corr * wb;  b.y -= dy * corr * wb;
    }

    // Unilateral min-distance constraints (joint angle limits).
    for (const c of minConstraints) {
      const a = at(p, c.i), b = at(p, c.j);
      const dx = b.x - a.x, dy = b.y - a.y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= c.min * c.min) continue;
      const d = Math.sqrt(d2) || 1e-6;
      const corr = (d - c.min) / d;
      const wa = b.mass / (a.mass + b.mass);
      const wb = 1 - wa;
      a.x += dx * corr * wa;  a.y += dy * corr * wa;
      b.x -= dx * corr * wb;  b.y -= dy * corr * wb;
    }

    // Ground collision — push each particle above terrain, apply bounce+friction.
    for (const pt of p) {
      const gy = terrainY(pt.x);
      if (pt.y > gy) {
        const vx = pt.x - pt.px, vy = pt.y - pt.py;
        pt.y  = gy;
        pt.py = gy + vy * 0.14;   // ~14% normal restitution
        pt.px = pt.x - vx * 0.68; // 32% horizontal friction on ground contact
      }
    }
  }
}

function drawGullRagdoll(rdoll: GullRagdoll): void {
  const { p } = rdoll;
  const sz = GULL_SZ;

  // Body axis direction (P3 → P1).
  const bDX = p[1].x - p[3].x, bDY = p[1].y - p[3].y;
  const bLen = Math.sqrt(bDX * bDX + bDY * bDY) || 1;
  const bAng = Math.atan2(bDY, bDX);

  ctx.lineCap = 'round';  ctx.lineJoin = 'round';

  // ── Far (back) wing — reflected across body axis, muted ──────────────────
  const wDX = p[5].x - p[0].x, wDY = p[5].y - p[0].y;
  const bAxX = bDX / bLen, bAxY = bDY / bLen;
  const dot  = wDX * bAxX + wDY * bAxY;
  // Reflect wing tip across body axis and scale down slightly (far wing smaller)
  const bwX = p[0].x + dot * bAxX - (wDX - dot * bAxX) * 0.55;
  const bwY = p[0].y + dot * bAxY - (wDY - dot * bAxY) * 0.55;
  const bwMX = (p[0].x + bwX) * 0.5 - bAxY * sz * 0.12;
  const bwMY = (p[0].y + bwY) * 0.5 + bAxX * sz * 0.12;
  ctx.beginPath();
  ctx.moveTo(p[0].x, p[0].y);
  ctx.quadraticCurveTo(bwMX, bwMY, bwX, bwY);
  ctx.strokeStyle = '#9eadb8';  ctx.lineWidth = sz * 0.50;  ctx.stroke();

  // ── Tail ─────────────────────────────────────────────────────────────────
  const tDX = p[4].x - p[3].x, tDY = p[4].y - p[3].y;
  const tLen = Math.sqrt(tDX * tDX + tDY * tDY) || 1;
  const tPX = -tDY / tLen * sz * 0.18, tPY = tDX / tLen * sz * 0.18;
  ctx.beginPath();
  ctx.moveTo(p[3].x, p[3].y);
  ctx.lineTo(p[4].x + tPX, p[4].y + tPY);
  ctx.lineTo(p[4].x - tPX, p[4].y - tPY);
  ctx.closePath();
  ctx.fillStyle = '#e8ecef';  ctx.fill();

  // ── Body ellipse (oriented along P3–P1 axis) ──────────────────────────────
  ctx.save();
  ctx.translate(p[0].x, p[0].y);
  ctx.rotate(bAng);
  ctx.beginPath();
  ctx.ellipse(0, 0, bLen * 0.50, sz * 0.40, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#e8ecef';  ctx.fill();
  ctx.restore();

  // ── Front wing ───────────────────────────────────────────────────────────
  const fwDX = p[5].x - p[1].x, fwDY = p[5].y - p[1].y;
  const fwLen = Math.sqrt(fwDX * fwDX + fwDY * fwDY) || 1;
  const fwMX = (p[1].x + p[5].x) * 0.5 - fwDY / fwLen * sz * 0.22;
  const fwMY = (p[1].y + p[5].y) * 0.5 + fwDX / fwLen * sz * 0.22;
  ctx.beginPath();
  ctx.moveTo(p[1].x, p[1].y);
  ctx.quadraticCurveTo(fwMX, fwMY, p[5].x, p[5].y);
  ctx.strokeStyle = '#d6e3ec';  ctx.lineWidth = sz * 0.58;  ctx.stroke();

  // ── Head ──────────────────────────────────────────────────────────────────
  ctx.beginPath();
  ctx.arc(p[2].x, p[2].y, sz * 0.36, 0, Math.PI * 2);
  ctx.fillStyle = '#e8ecef';  ctx.fill();

  // Beak — projects from head in the neck→head direction.
  const hDX = p[2].x - p[1].x, hDY = p[2].y - p[1].y;
  const hLen = Math.sqrt(hDX * hDX + hDY * hDY) || 1;
  const hnX = hDX / hLen, hnY = hDY / hLen;
  const hpX = -hnY * sz * 0.12, hpY = hnX * sz * 0.12;
  const bkBase = 0.25 * sz, bkTip = 0.65 * sz;
  ctx.beginPath();
  ctx.moveTo(p[2].x + hnX * bkBase + hpX, p[2].y + hnY * bkBase + hpY);
  ctx.lineTo(p[2].x + hnX * bkTip,        p[2].y + hnY * bkTip);
  ctx.lineTo(p[2].x + hnX * bkBase - hpX, p[2].y + hnY * bkBase - hpY);
  ctx.closePath();
  ctx.fillStyle = '#d08018';  ctx.fill();

  // ── X eyes ────────────────────────────────────────────────────────────────
  const er = sz * 0.10;
  ctx.strokeStyle = '#cc0000';  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(p[2].x - er, p[2].y - er); ctx.lineTo(p[2].x + er, p[2].y + er); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(p[2].x + er, p[2].y - er); ctx.lineTo(p[2].x - er, p[2].y + er); ctx.stroke();

  // ── Legs (stub lines from body centre to feet) ────────────────────────────
  ctx.strokeStyle = '#c4a060';  ctx.lineWidth = 1.5;
  for (const foot of [p[6], p[7]]) {
    ctx.beginPath();
    ctx.moveTo(p[0].x, p[0].y);
    ctx.lineTo(foot.x, foot.y);
    ctx.stroke();
    // Toe stubs when foot is near terrain
    const gy = terrainY(foot.x);
    if (foot.y > gy - sz * 0.15) {
      ctx.beginPath();
      ctx.moveTo(foot.x - sz * 0.20, foot.y);
      ctx.lineTo(foot.x + sz * 0.16, foot.y);
      ctx.stroke();
    }
  }
}

// ─── Seagulls ─────────────────────────────────────────────────────────────────
function drawGull(g: Gull): void {
  // Visibility cull in screen space
  const sx = g.x - camera.x, sy = g.y - camera.y;
  if (sx < -100 || sx > VIEW_W + 100 || sy < -100 || sy > VIEW_H + 100) return;

  // Dead gulls are rendered entirely by the ragdoll system.
  if (g.state === 'dead') {
    if (g.ragdoll) drawGullRagdoll(g.ragdoll);
    return;
  }

  const sz = GULL_SZ;

  // Wing flap factor: +1 = full up, -1 = full down
  let wf: number;
  if      (g.state === 'landed')  wf = -0.55;
  else if (g.state === 'gliding') wf =  0.18;
  else                            wf = Math.sin(g.wingPhase);

  // Wing-tip Y positions (in local coords, -y = up)
  const wTipY1 = -sz * (0.85 + wf * 1.15);   // front wing tip
  const wTipY2 = -sz * (0.45 + wf * 0.78);   // back wing tip

  ctx.save();
  ctx.translate(g.x, g.y);          // world-space position (ctx already translated)
  if (g.dir < 0) ctx.scale(-1, 1);

  // Back wing (darker, partially occluded by body)
  ctx.beginPath();
  ctx.moveTo(-sz * 0.15, -sz * 0.26);
  ctx.quadraticCurveTo(-sz * 0.38, wTipY2 - sz * 0.08, sz * 0.52, wTipY2);
  ctx.quadraticCurveTo(sz * 0.22, -sz * 0.06, -sz * 0.15, -sz * 0.26);
  ctx.fillStyle = '#9eadb8';
  ctx.fill();

  // Tail
  ctx.beginPath();
  ctx.moveTo(-sz * 0.92, sz * 0.07);
  ctx.lineTo(-sz * 1.72, -sz * 0.17);
  ctx.lineTo(-sz * 1.60, sz * 0.30);
  ctx.closePath();
  ctx.fillStyle = '#e8ecef';
  ctx.fill();

  // Body
  ctx.beginPath();
  ctx.ellipse(-sz * 0.05, sz * 0.05, sz * 1.0, sz * 0.40, -0.06, 0, Math.PI * 2);
  ctx.fillStyle = '#e8ecef';
  ctx.fill();

  // Front wing (lighter, drawn over body)
  ctx.beginPath();
  ctx.moveTo(-sz * 0.05, -sz * 0.30);
  ctx.quadraticCurveTo(-sz * 0.22, wTipY1 - sz * 0.12, sz * 0.72, wTipY1);
  ctx.quadraticCurveTo(sz * 0.32, -sz * 0.07, -sz * 0.05, -sz * 0.30);
  ctx.fillStyle = '#d6e3ec';
  ctx.fill();

  // Head
  ctx.beginPath();
  ctx.arc(sz * 0.95, -sz * 0.31, sz * 0.36, 0, Math.PI * 2);
  ctx.fillStyle = '#e8ecef';
  ctx.fill();

  // Beak (orange body + yellow-orange tip stripe)
  ctx.beginPath();
  ctx.moveTo(sz * 1.23, -sz * 0.40);
  ctx.lineTo(sz * 1.86, -sz * 0.26);
  ctx.lineTo(sz * 1.23, -sz * 0.12);
  ctx.closePath();
  ctx.fillStyle = '#d08018';
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(sz * 1.62, -sz * 0.25);
  ctx.lineTo(sz * 1.86, -sz * 0.26);
  ctx.lineTo(sz * 1.23, -sz * 0.12);
  ctx.lineTo(sz * 1.46, -sz * 0.17);
  ctx.closePath();
  ctx.fillStyle = '#f5c030';
  ctx.fill();

  // Eye + highlight
  ctx.beginPath();
  ctx.arc(sz * 1.02, -sz * 0.38, sz * 0.09, 0, Math.PI * 2);
  ctx.fillStyle = '#111';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(sz * 1.00, -sz * 0.40, sz * 0.04, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fill();

  // Legs — show when on ground or in final 50 px of approach / early takeoff
  const gAltDraw = terrainY(g.x) - g.y;
  if (g.state === 'landed' || g.state === 'takingoff' ||
      ((g.state === 'landing') && gAltDraw < 50)) {
    ctx.strokeStyle = '#c4a060';
    ctx.lineWidth   = 1.5;
    ctx.lineCap     = 'round';
    for (const lx of [sz * 0.25, -sz * 0.18]) {
      const ankleX = lx - sz * 0.04, ankleY = sz * 0.84;
      ctx.beginPath();
      ctx.moveTo(lx, sz * 0.38);
      ctx.lineTo(ankleX, ankleY);
      ctx.moveTo(ankleX, ankleY); ctx.lineTo(ankleX - sz * 0.20, ankleY);  // back toe
      ctx.moveTo(ankleX, ankleY); ctx.lineTo(ankleX + sz * 0.16, ankleY);  // front toe
      ctx.stroke();
    }
  }

  ctx.restore();

  // Health bar (world space, above all live gulls; the dead returned above)
  const barW = sz * 3.2;
  const barH = 4;
  const barX = g.x - barW / 2;
  const barY = g.y - sz * 3.4;
  ctx.fillStyle = 'rgba(0,0,0,0.50)';
  ctx.fillRect(barX - 1, barY - 1, barW + 2, barH + 2);
  const pct = g.health / 100;
  ctx.fillStyle = pct > 0.5 ? '#dd1111' : '#ff5520';
  ctx.fillRect(barX, barY, barW * pct, barH);
}

// Pop balloon at index idx: record animation, remove from rope list.
function popBalloon(idx: number): void {
  const tip = tipOf(at(playerBalloons, idx));
  pops.push({ x: tip.x, y: tip.y, t: 0 });
  playBalloonPop(tip.x, tip.y);
  playerBalloons.splice(idx, 1);
}

// Choose a fresh dive angle and approach position for gull g.
// Must be called while playerBalloons.length > 0.
function chooseDive(g: Gull): void {
  g.diveBalloonIdx = Math.floor(Math.random() * playerBalloons.length);
  const tip = tipOf(at(playerBalloons, g.diveBalloonIdx));
  // Angle in (π/4, 3π/4): ±45° of straight-down in canvas coords
  const sign = Math.random() < 0.5 ? 1 : -1;
  g.diveAngle = Math.PI / 2 + sign * Math.random() * (Math.PI / 4);
  // Approach position: directly upstream from the dive direction
  const D = 120;
  g.diveTgt = {
    x: tip.x - Math.cos(g.diveAngle) * D,
    y: Math.min(tip.y - Math.sin(g.diveAngle) * D, player.y - 160),
  };
}

// Recompute diveTgt from current balloon position with player-velocity lead.
// Lerps the stored target to avoid whipsawing the bird on fast player movement.
function updateDiveTgt(g: Gull): void {
  if (playerBalloons.length === 0) return;
  const idx = Math.min(g.diveBalloonIdx, playerBalloons.length - 1);
  const tip = tipOf(at(playerBalloons, idx));
  const LEAD = 18;  // ticks of look-ahead
  const tx = tip.x + player.vx * LEAD;
  const ty = tip.y + player.vy * LEAD;
  const D  = 120;
  const nx = tx - Math.cos(g.diveAngle) * D;
  const ny = Math.min(ty - Math.sin(g.diveAngle) * D, player.y - 140);
  // Smooth target tracking — prevents steering oscillation from sudden jumps
  g.diveTgt.x += (nx - g.diveTgt.x) * 0.06;
  g.diveTgt.y += (ny - g.diveTgt.y) * 0.06;
}

function updateGulls(): void {
  // Spawn replacements spread around the player (don't count dead ragdolls)
  while (gulls.filter(g => g.state !== 'dead').length < GULL_COUNT) {
    // Spawn anywhere in cached terrain but outside the camera viewport.
    const camL = camera.x, camR = camera.x + VIEW_W;
    const leftW  = Math.max(0, camL - terrainStartX);
    const rightW = Math.max(0, terrainEndX() - camR);
    const total  = leftW + rightW;
    let spawnX: number;
    if (total < 1) {
      // Terrain barely extends beyond camera — fall back to just off-screen
      spawnX = player.x + (Math.random() < 0.5 ? -1 : 1) * VIEW_W * 1.5;
    } else {
      if (Math.random() * total < leftW) {
        spawnX = terrainStartX + Math.random() * leftW;   // left of camera
      } else {
        spawnX = camR + Math.random() * rightW;           // right of camera
      }
    }
    gulls.push(makeGull(spawnX));
  }

  const repulseH = GULL_ALT_MIN * 0.55;  // start pushing up below this altitude

  for (let i: number = gulls.length - 1; i >= 0; i--) {
    const g = at(gulls, i);

    // Cull when far from player
    if (Math.abs(g.x - player.x) > KEEP_RANGE * VIEW_W) {
      gulls.splice(i, 1);
      continue;
    }

    const groundY  = terrainY(g.x);
    const standY   = groundY - GULL_SZ;       // center Y when standing on ground
    const altAbove = groundY - g.y;           // height above terrain (positive = up)

    g.stateTimer++;
    g.spawnAge++;
    if (g.diveCooldown > 0) g.diveCooldown--;

    // Divebomb trigger: bird in the centre half of the 1920 px viewport (in
    // viewport units, not the glass, so every window plays the same game),
    // player has balloons, and bird has been alive at least 6 s (prevents
    // beelining on spawn).
    if (g.state !== 'dead' && g.state !== 'divebomb' && playerBalloons.length > 0 && g.spawnAge > 360) {
      const screenGX = g.x - camera.x;
      if (screenGX >= VIEW_W / 4 && screenGX <= 3 * VIEW_W / 4) {
        g.state       = 'divebomb';
        g.divePhase   = 3;   // circle for 10 s before first dive
        g.stateTimer  = 0;
        g.diveCooldown = 600; // 10 s at 60 fps
        g.circleDir   = g.vx >= 0 ? 1 : -1;
        g.circleAngle = Math.atan2(g.y - (player.y - 300), g.x - player.x);
      }
    }

    // If player lost all balloons, divebombing birds return to flying
    if (g.state === 'divebomb' && playerBalloons.length === 0) {
      g.state = 'flying';
      g.stateTimer = 0;
    }

    switch (g.state) {

      case 'flying': {
        g.wingPhase += g.wingSpeed;

        // Altitude correction: target a climb/descent rate rather than applying
        // a raw proportional force — this caps vertical speed and prevents
        // the gull from rocketing upward when the error is large (e.g. just
        // after takeoff when targetAlt is far above current altitude).
        const altError    = (groundY - g.targetAlt) - g.y;
        const climbTarget = Math.max(-1.2, Math.min(1.2, altError * 0.006));
        g.vy += (climbTarget - g.vy) * 0.032;

        // Gentle ground repulsion — just a soft nudge, not a hard shove
        if (altAbove < repulseH)
          g.vy -= (1 - altAbove / repulseH) * 0.10;

        // Catch-up: gentle speed boost toward player when balloons exist.
        // No direction flip — birds wander naturally; the flip caused beelining on spawn.
        const dxToPlayer  = player.x - g.x;
        const catchup     = playerBalloons.length > 0
          ? Math.min(Math.abs(dxToPlayer) / (VIEW_W * 0.5), 3.0)
          : 0;
        const effectiveMax = GULL_SPD_MAX * (1 + catchup * 0.55);

        // Horizontal cruise — gently track a target in the allowed speed band
        const cruiseSpd = GULL_SPD_MIN + (Math.sin(g.stateTimer * 0.018) * 0.5 + 0.5)
                        * (effectiveMax - GULL_SPD_MIN);
        const cruiseVx = g.dir * cruiseSpd;
        g.vx += (cruiseVx - g.vx) * 0.028;
        // Hard-clamp so wind/collision never pushes outside the speed band
        const spdAbs = Math.abs(g.vx);
        if (spdAbs > effectiveMax) g.vx = g.dir * effectiveMax;
        if (spdAbs < GULL_SPD_MIN) g.vx = g.dir * GULL_SPD_MIN;

        g.vx *= 0.998; g.vy *= 0.970;

        if (Math.random() < 0.003) g.dir *= -1;                           // direction flip
        if (Math.random() < 0.006 && g.stateTimer > 60)  { g.state = 'gliding';  g.stateTimer = 0; }
        if (Math.random() < 0.0007 && g.stateTimer > 200) { g.state = 'landing'; g.stateTimer = 0; }
        break;
      }

      case 'gliding': {
        g.wingPhase += g.wingSpeed * 0.06;  // wings nearly still
        g.vy += 0.032;                       // gentle sink
        g.vx *= 0.997; g.vy *= 0.986;

        if (altAbove < repulseH) { g.state = 'flying'; g.stateTimer = 0; }
        if (g.stateTimer > 100 + Math.random() * 120) { g.state = 'flying'; g.stateTimer = 0; }
        break;
      }

      case 'landing': {
        // Slow wing beats during approach, quicker flare when close
        g.wingPhase += g.wingSpeed * (altAbove < 40 ? 0.7 : 0.22);

        // Target sink rate scales with altitude — caps at 2 px/tick so the
        // glide is long and shallow, then bleeds off near the ground
        const targetVy = Math.min(altAbove * 0.03, 2.0);
        g.vy += (targetVy - g.vy) * 0.04;

        // Bleed horizontal speed only in the final 120 px of approach
        const closeness = Math.max(0, 1 - altAbove / 120);
        g.vx *= (0.997 - closeness * 0.045);
        g.vy *= 0.98;

        if (g.y >= standY - 2) {
          g.y = standY; g.vy = 0; g.vx = 0;
          g.state = 'landed';
          g.stateTimer = 0;
          g.landedDur  = 180 + Math.floor(Math.random() * 480); // 3–11 s
        }
        break;
      }

      case 'landed': {
        g.y = standY; g.vy = 0;

        // Occasional walk
        if (Math.random() < 0.012) {
          const wd = Math.random() < 0.5 ? 1 : -1;
          g.vx = wd * (0.35 + Math.random() * 0.65);
          g.dir = wd;
        }
        g.vx *= 0.84;
        g.x  += g.vx;
        if (Math.abs(g.vx) > 0.12) g.wingPhase += 0.18;  // walk-cycle for leg anim

        if (g.stateTimer >= g.landedDur) {
          g.state = 'takingoff'; g.stateTimer = 0;
          g.vy = -0.4; g.vx = g.dir * 0.2; g.wingPhase = 0;  // gentle hop to start
        }
        break;
      }

      case 'takingoff': {
        // Wing-beat intensity and lift ramp up gradually over ~2 s
        const prog = Math.min(g.stateTimer / 120, 1.0);
        g.wingPhase += g.wingSpeed * (0.7 + prog * 1.6);

        g.vy -= 0.04 + prog * 0.18;          // lift grows with time
        g.vx += g.dir * (0.01 + prog * 0.04);
        g.vx *= 0.988; g.vy *= 0.94;
        // Stay within speed band once airborne enough
        if (Math.abs(g.vx) > GULL_SPD_MAX) g.vx = g.dir * GULL_SPD_MAX;

        if (altAbove > 80) {
          g.state     = 'flying';
          g.targetAlt = GULL_ALT_MIN + Math.random() * (GULL_ALT_MAX - GULL_ALT_MIN);
          g.stateTimer = 0;
        }
        break;
      }

      case 'divebomb': {
        g.wingPhase += g.wingSpeed * 1.4;  // rapid wingbeats throughout

        // Shared dive speed — consistent across all active phases so there's no
        // abrupt jump as the bird transitions from approach → dive → pullup.
        const DIVE_SPD = 9.0;

        if (g.divePhase === 0) {
          // Approach: steer toward diveTgt at DIVE_SPD, updated live each tick.
          // The approach always follows the circling phase, whose speed cap is
          // below DIVE_SPD, so the bird only ever speeds up into it.
          updateDiveTgt(g);
          const dx = g.diveTgt.x - g.x;
          const dy = g.diveTgt.y - g.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          g.vx += (dx / dist * DIVE_SPD - g.vx) * 0.12;
          g.vy += (dy / dist * DIVE_SPD - g.vy) * 0.12;
          g.dir = dx >= 0 ? 1 : -1;
          if (dist < 30 || g.stateTimer > 300) {
            g.divePhase = 1;
            g.stateTimer = 0;
          }
        } else if (g.divePhase === 1) {
          // Dive: steer toward balloon tip with velocity lead at DIVE_SPD
          if (playerBalloons.length > 0) {
            const idx = Math.min(g.diveBalloonIdx, playerBalloons.length - 1);
            const tip  = tipOf(at(playerBalloons, idx));
            const LEAD = 12;
            const tx   = tip.x + player.vx * LEAD;
            const ty   = tip.y + player.vy * LEAD;
            const ddx  = tx - g.x, ddy = ty - g.y;
            const ddist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            g.vx += (ddx / ddist * DIVE_SPD - g.vx) * 0.15;
            g.vy += (ddy / ddist * DIVE_SPD - g.vy) * 0.15;
          }
          g.dir = g.vx >= 0 ? 1 : -1;

          // Beak vs balloon collision — beak points along direction of travel
          if (g.diveCooldown === 0 && playerBalloons.length > 0) {
            const sz   = GULL_SZ;
            const bspd = Math.sqrt(g.vx * g.vx + g.vy * g.vy) || 1;
            const beakX = g.x + (g.vx / bspd) * sz * 1.65;
            const beakY = g.y + (g.vy / bspd) * sz * 1.65;
            for (let bi: number = playerBalloons.length - 1; bi >= 0; bi--) {
              const tip = tipOf(at(playerBalloons, bi));
              const bdx = beakX - tip.x, bdy = beakY - tip.y;
              if (bdx * bdx + bdy * bdy < (BALLOON_RADIUS + 8) * (BALLOON_RADIUS + 8)) {
                popBalloon(bi);
                g.diveCooldown = 900;  // 15 s at 60 fps
                break;
              }
            }
          }

          // Transition to pullup after passing balloon zone or timeout
          if (g.vy < 0 || g.stateTimer > 90) {
            g.divePhase = 2;
            g.stateTimer = 0;
          }
        } else if (g.divePhase === 2) {
          // Pullup: arc upward at same speed, preserving horizontal lean.
          // Target a velocity that's mostly upward with a slight horizontal carry —
          // same magnitude as DIVE_SPD so there's no speed discontinuity.
          const lean  = (g.vx >= 0 ? 1 : -1) * 0.35;  // gentle sideways lean
          const lMag  = Math.sqrt(1 + lean * lean);
          const tvx   = lean / lMag * DIVE_SPD;
          const tvy   = -1   / lMag * DIVE_SPD;
          g.vx += (tvx - g.vx) * 0.10;
          g.vy += (tvy - g.vy) * 0.10;
          if (g.vy < -DIVE_SPD * 0.6 || g.stateTimer > 80) {
            g.divePhase   = 3;
            g.stateTimer  = 0;
            // Always circle for 10 s after a dive, whether hit or miss
            if (g.diveCooldown < 600) g.diveCooldown = 600;
            // circleDir frozen from current horizontal velocity — never re-derived
            g.circleDir   = g.vx >= 0 ? 1 : -1;
            g.circleAngle = Math.atan2(g.y - (player.y - 300), g.x - player.x);
          }
        } else if (g.divePhase === 3) {
          // Desired position: 300px above player, orbiting at CIRCLE_R.
          // Reached entirely via spring forces — no direct position assignment.
          // The desired centre tracks the player each tick; the bird follows via physics.
          const CIRCLE_R  = 160;
          g.circleAngle  += 0.012 * g.circleDir;
          const desiredCX = player.x;
          const desiredCY = player.y - 300;
          const orbitX    = desiredCX + Math.cos(g.circleAngle) * CIRCLE_R;
          const orbitY    = desiredCY + Math.sin(g.circleAngle) * 55;
          g.vx += (orbitX - g.x) * 0.005;
          g.vy += (orbitY - g.y) * 0.005;
          // Speed cap — bird can't teleport even if orbit target is far away
          const spd3 = Math.sqrt(g.vx * g.vx + g.vy * g.vy);
          if (spd3 > GULL_SPD_MAX) { g.vx = g.vx / spd3 * GULL_SPD_MAX; g.vy = g.vy / spd3 * GULL_SPD_MAX; }
          g.vx *= 0.84; g.vy *= 0.84;
          // dir from orbit tangent — smooth, never flips mid-circle
          g.dir = (-Math.sin(g.circleAngle) * g.circleDir) >= 0 ? 1 : -1;

          if (g.diveCooldown === 0) {
            // Cooldown expired — dive again if balloons remain, else return to flying
            if (playerBalloons.length > 0) {
              g.divePhase = 0;
              g.stateTimer = 0;
              chooseDive(g);
            } else {
              g.state      = 'flying';
              g.stateTimer = 0;
              g.targetAlt  = GULL_ALT_MIN + Math.random() * (GULL_ALT_MAX - GULL_ALT_MIN);
              if (Math.abs(g.vx) < GULL_SPD_MIN) g.vx = g.dir * GULL_SPD_MIN;
            }
          }
        }
        break;
      }

      case 'dead': {
        // Ensure ragdoll exists (safety fallback if death was triggered elsewhere)
        g.ragdoll ??= makeGullRagdoll(g);
        updateGullRagdoll(g.ragdoll);
        // Keep g.x/g.y on the body-centre particle for culling and health-bar logic
        g.x = g.ragdoll.p[0].x;
        g.y = g.ragdoll.p[0].y;
        // Despawn after ~1 minute or if body drifts outside cached terrain
        if (g.stateTimer > 3600 || g.x < terrainStartX || g.x > terrainEndX()) {
          gulls.splice(i, 1); continue;
        }
        break;
      }
    }

    // Integrate (landed and dead manage their own position)
    if (g.state !== 'landed' && g.state !== 'dead') {
      g.x += g.vx;
      g.y += g.vy;
      // Prevent going underground — but don't override divebombing birds
      if (g.y > standY && g.state !== 'divebomb') {
        g.y = standY; g.vy = 0;
        g.state = 'landed'; g.vx = 0;
        g.landedDur = 120 + Math.floor(Math.random() * 240);
        g.stateTimer = 0;
      }
    }
  }
}

// ─── Slingshot balls ──────────────────────────────────────────────────────────

// A first contact along a sweep: how far along it (0..1) and the surface normal there.
interface Contact { t: number; nx: number; ny: number }

// Swept circle vs AABB — returns the first contact in [0,1], or null.
// Circle starts at (cx,cy), moves by (dx,dy), radius r.
// AABB: [bx, bx+bw] × [by, by+bh].
// Uses Minkowski expansion: 4 face slabs + 4 corner circles.
function sweepCircleBox(
  cx: number, cy: number, dx: number, dy: number, r: number, bx: number, by: number, bw: number, bh: number,
): Contact | null {
  let tMin: number = Infinity, nnx: number = 0, nny: number = 0;
  const eps  = 1e-8;
  const tEps = 1e-4; // ignore contacts at t≈0 (ball already touching surface)

  // Face slab tests — each face expanded outward by r
  if (Math.abs(dx) > eps) {
    // Left face (normal −x)
    let t: number = (bx - r - cx) / dx;
    if (t > tEps) { const hy = cy + t * dy; if (hy >= by && hy <= by + bh && t < tMin) { tMin = t; nnx = -1; nny = 0; } }
    // Right face (normal +x)
    t = (bx + bw + r - cx) / dx;
    if (t > tEps) { const hy = cy + t * dy; if (hy >= by && hy <= by + bh && t < tMin) { tMin = t; nnx =  1; nny = 0; } }
  }
  if (Math.abs(dy) > eps) {
    // Top face (normal −y)
    let t: number = (by - r - cy) / dy;
    if (t > tEps) { const hx = cx + t * dx; if (hx >= bx && hx <= bx + bw && t < tMin) { tMin = t; nnx = 0; nny = -1; } }
    // Bottom face (normal +y)
    t = (by + bh + r - cy) / dy;
    if (t > tEps) { const hx = cx + t * dx; if (hx >= bx && hx <= bx + bw && t < tMin) { tMin = t; nnx = 0; nny =  1; } }
  }

  // Corner circle tests
  const a = dx * dx + dy * dy;
  if (a > eps) {
    const corners: Point[] = [[bx, by], [bx + bw, by], [bx, by + bh], [bx + bw, by + bh]];
    for (const [corX, corY] of corners) {
      const fcx = cx - corX, fcy = cy - corY;
      const halfB = fcx * dx + fcy * dy;
      const c     = fcx * fcx + fcy * fcy - r * r;
      const disc  = halfB * halfB - a * c;
      if (disc < 0) continue;
      const t = (-halfB - Math.sqrt(disc)) / a;
      if (t <= tEps || t >= tMin) continue;
      tMin = t;
      const hx = cx + t * dx - corX, hy = cy + t * dy - corY;
      const hl = Math.sqrt(hx * hx + hy * hy) || 1;
      nnx = hx / hl; nny = hy / hl;
    }
  }

  if (!isFinite(tMin) || tMin > 1) return null;
  return { t: tMin, nx: nnx, ny: nny };
}

// A grounded ball moving slower than this for REST_FRAMES in a row is at
// rest: its physics stop and its rolling loop is released. It still ages
// out and is still drawn; only a fresh hit could move it, and nothing
// hits balls, so rest is final.
const REST_EPSILON: number = 0.05;
const REST_FRAMES: number  = 30;

// Coming down on the terrain or a crate top faster than this, a ball bounces;
// slower, it lands and rolls.
const BALL_SETTLE_VY: number = 1.8;

// The top face of a crate, in world y.
function boxTopY(box: Box): number {
  return Math.max(terrainY(box.x), terrainY(box.x + BOX_SIZE)) - BOX_SIZE;
}

// A ball that comes down gently on a crate's top sits on it, as it would on the
// terrain, and rolls there until it leaves the edge. A ball that only ever
// bounced on a crate never counted as grounded, so it jittered there until it
// aged out, never came to rest and never rolled with the crate's sound.
function settleOnBox(b: Ball, box: Box): void {
  b.y = boxTopY(box) - BALL_RADIUS;
  b.vy = 0;
  b.grounded = true;
  b.onBox = box;
}

function updateBalls(): void {
  for (let i: number = balls.length - 1; i >= 0; i--) {
    const b = at(balls, i);
    if (b.resting === true) {
      b.age++;
      if (b.age > 3600) { balls.splice(i, 1); }
      continue;
    }
    if (b.onBox && (b.x < b.onBox.x || b.x > b.onBox.x + BOX_SIZE)) {
      b.onBox = null;          // rolled off the edge of the crate
      b.grounded = false;
    }
    const prevGrounded = b.grounded;

    if (b.grounded && b.onBox) {
      // Roll along the crate top, which is flat: only the drag acts
      b.vx *= BALL_ROLL_DRAG;
      b.y   = boxTopY(b.onBox) - BALL_RADIUS;
    } else if (b.grounded) {
      // Roll along terrain under gravity
      const slope = (terrainY(b.x + 1) - terrainY(b.x - 1)) / 2;
      const nlen  = Math.sqrt(1 + slope * slope);
      b.vx += GRAVITY * slope / nlen;
      b.vx *= BALL_ROLL_DRAG;
      b.y   = terrainY(b.x) - BALL_RADIUS;
    } else {
      b.vy += GRAVITY;
      b.vx *= BALL_DRAG;
      b.vy *= BALL_DRAG;
    }

    const prevX = b.x, prevY = b.y;
    b.x += b.vx;
    if (!b.grounded) b.y += b.vy;
    b.age++;
    b.grounded = false;

    // ── Terrain collision ──────────────────────────────────────────────────
    const gy = terrainY(b.x);
    if (b.y + BALL_RADIUS >= gy) {
      b.y = gy - BALL_RADIUS;
      if (b.vy > BALL_SETTLE_VY) {
        playBallBounce(b, 'ground', b.vy);
        b.vy = -b.vy * BALL_RESTITUTION;
        b.vx *= 0.82;
      } else {
        b.grounded = true;
        b.vy = 0;
        if (!prevGrounded) startBallRoll(b, 'ground');
      }
    }

    // Ball sweep AABB — used by box and gull loops for coarse early rejection
    const bAABBx0 = Math.min(prevX, b.x) - BALL_RADIUS;
    const bAABBx1 = Math.max(prevX, b.x) + BALL_RADIUS;
    const bAABBy0 = Math.min(prevY, b.y) - BALL_RADIUS;
    const bAABBy1 = Math.max(prevY, b.y) + BALL_RADIUS;

    // ── Box collisions (swept) ─────────────────────────────────────────────
    for (const box of boxes) {
      const boxY = boxTopY(box);

      // A ball rolling along this crate's top is still on it: the edge check at
      // the top of the tick would have let it go otherwise. Sitting exactly on
      // the face it neither sweeps into the crate nor overlaps it, so nothing
      // below would count it as grounded again.
      if (b.onBox === box) { b.grounded = true; continue; }

      // Coarse AABB rejection before expensive sweep
      if (bAABBx1 < box.x || bAABBx0 > box.x + BOX_SIZE ||
          bAABBy1 < boxY  || bAABBy0 > boxY  + BOX_SIZE) continue;

      const sdx = b.x - prevX, sdy = b.y - prevY;
      const hit = sweepCircleBox(prevX, prevY, sdx, sdy, BALL_RADIUS,
                                 box.x, boxY, BOX_SIZE, BOX_SIZE);
      if (hit) {
        // Place ball at contact surface
        b.x = prevX + hit.t * sdx;
        b.y = prevY + hit.t * sdy;
        const vn = b.vx * hit.nx + b.vy * hit.ny;
        if (vn < 0 && hit.ny < -0.5 && -vn <= BALL_SETTLE_VY) {
          settleOnBox(b, box);   // a gentle touchdown on the top: it lands
        } else if (vn < 0) {
          if (Math.abs(vn) >= 1.5) playBallBounce(b, 'box', Math.abs(vn));
          b.vx -= (1 + BALL_BOX_BOUNCE) * vn * hit.nx;
          b.vy -= (1 + BALL_BOX_BOUNCE) * vn * hit.ny;
          b.vx *= 0.85; b.vy *= 0.85;
          if (b.vy < -0.5) b.grounded = false;
        }
      } else if (circleOverlapsBox(b.x, b.y, BALL_RADIUS, box.x, boxY, BOX_SIZE, BOX_SIZE)) {
        // Fallback: already overlapping — push out
        const nearX = Math.max(box.x, Math.min(b.x, box.x + BOX_SIZE));
        const nearY = Math.max(boxY,  Math.min(b.y, boxY  + BOX_SIZE));
        const ddx = b.x - nearX, ddy = b.y - nearY;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy) || 0.001;
        const nx = ddx / dist, ny = ddy / dist;
        b.x += nx * (BALL_RADIUS - dist);
        b.y += ny * (BALL_RADIUS - dist);
        const vn = b.vx * nx + b.vy * ny;
        if (vn < 0 && ny < -0.5 && -vn <= BALL_SETTLE_VY) {
          settleOnBox(b, box);
        } else if (vn < 0) {
          if (Math.abs(vn) >= 1.5) playBallBounce(b, 'box', Math.abs(vn));
          b.vx -= (1 + BALL_BOX_BOUNCE) * vn * nx;
          b.vy -= (1 + BALL_BOX_BOUNCE) * vn * ny;
          b.vx *= 0.85; b.vy *= 0.85;
          if (b.vy < -0.5) b.grounded = false;
        }
      }
    }

    // ── Gull collisions (relative-sweep segment vs per-part circles) ──────
    // The gull moved (g.vx, g.vy) this tick (applied in updateGulls before
    // updateBalls runs).  Doing the sweep in the gull's reference frame —
    // subtracting gull velocity from ball velocity, testing against the gull
    // part at its END position — gives a correct swept test even when the two
    // objects are closing fast head-on (e.g. divebomb vs fired ball).
    const ballDx = b.x - prevX, ballDy = b.y - prevY;
    for (const g of gulls) {
      // Coarse AABB rejection: conservative box covering body+wings+head+tail
      // Symmetric on x (dir can flip), wings extend ~2×sz up, body+tail ~1×sz down
      const gAABBHalfW = GULL_SZ * 2.0;
      const gAABBTop   = g.y - GULL_SZ * 2.2;
      const gAABBBot   = g.y + GULL_SZ * 1.0;
      // Expand coarse AABB by gull's own motion to catch relative-sweep cases
      const gvAbs = Math.abs(g.vx) + Math.abs(g.vy);
      if (bAABBx1 < g.x - gAABBHalfW - gvAbs || bAABBx0 > g.x + gAABBHalfW + gvAbs ||
          bAABBy1 < gAABBTop - gvAbs           || bAABBy0 > gAABBBot + gvAbs) continue;

      const df  = g.dir;   // ±1 matches drawGull's ctx.scale(-1,1)
      const sz  = GULL_SZ;
      const wf  = g.state === 'landed'  ? -0.55
                : g.state === 'gliding' ?  0.18
                : Math.sin(g.wingPhase);

      // Body parts in local space [localX (pre-flip), localY, radius, restitution]
      // Restitution reflects how elastic each part is:
      //   tail < body < head < wing (feathers spring back most)
      if (g.state === 'dead') continue;

      const parts = [
        { lx: -sz*0.05, ly:  sz*0.05,              r: sz*0.82, rest: 0.28, dmg: 0.5 }, // body
        { lx:  sz*0.95, ly: -sz*0.31,              r: sz*0.36, rest: 0.55, dmg: 2.0 }, // head
        { lx:  sz*0.30, ly: -(sz*0.55+wf*sz*0.85), r: sz*0.52, rest: 0.68, dmg: 0.3 }, // wing
        { lx: -sz*1.30, ly:  sz*0.06,              r: sz*0.22, rest: 0.18, dmg: 0.2 }, // tail
      ];

      for (const part of parts) {
        const wx   = g.x + part.lx * df;  // gull part END position
        const wy   = g.y + part.ly;
        const minR = BALL_RADIUS + part.r;

        // Relative sweep: ball motion minus gull motion, gull part treated as
        // stationary at its end position.  Catches head-on tunneling.
        const rdx = ballDx - g.vx, rdy = ballDy - g.vy;
        const fx  = prevX - wx,    fy  = prevY - wy;
        const sA  = rdx*rdx + rdy*rdy;
        let hitT: number = -1;
        if (sA > 1e-8) {
          const hB   = fx*rdx + fy*rdy;
          const hC   = fx*fx + fy*fy - minR*minR;
          const disc = hB*hB - sA*hC;
          if (disc >= 0) {
            const ts = (-hB - Math.sqrt(disc)) / sA;
            if (ts > 1e-4 && ts <= 1) hitT = ts;
          }
        }

        // Overlap fallback for slow or already-touching balls
        const cdx = b.x - wx, cdy = b.y - wy;
        const cDist = Math.sqrt(cdx*cdx + cdy*cdy);
        if (hitT < 0 && cDist >= minR) continue;

        // Derive contact normal from the relative-frame hit point
        let nx: number, ny: number;
        if (hitT >= 0) {
          // World position of ball at hit time
          const bHitX = prevX + hitT * ballDx;
          const bHitY = prevY + hitT * ballDy;
          // World position of gull part at hit time
          const gHitX = wx - g.vx + hitT * g.vx;
          const gHitY = wy - g.vy + hitT * g.vy;
          const hx = bHitX - gHitX, hy = bHitY - gHitY;
          const hl = Math.sqrt(hx*hx + hy*hy) || 1;
          nx = hx/hl; ny = hy/hl;
          b.x = bHitX;
          b.y = bHitY;
        } else {
          nx = cdx / (cDist || 1);
          ny = cdy / (cDist || 1);
          b.x += nx * (minR - cDist);
          b.y += ny * (minR - cDist);
        }

        const vn = b.vx*nx + b.vy*ny;
        if (vn < 0) {
          // Effective restitution scales down with damage: harder hits absorb more
          // energy and the ball bounces less.  part.dmg * |vn| / 40 = 1 at a
          // fully energy-absorbing impact (e.g. head hit at full speed).
          const absorbed    = Math.min(1, part.dmg * Math.abs(vn) / 40);
          const effectRest  = part.rest * (1 - absorbed);
          // Reflect ball with damage-scaled restitution + friction
          playBallBounce(b, 'bird', Math.abs(vn));
          b.vx -= (1 + effectRest) * vn * nx;
          b.vy -= (1 + effectRest) * vn * ny;
          b.vx *= 0.88; b.vy *= 0.88;
          b.grounded = false;

          // Transfer impulse to gull (gull ~15× ball mass → small fraction)
          const strength = -vn * 0.07;
          g.vx -= nx * strength;   // Newton 3rd law: gull pushed opposite to normal
          g.vy -= ny * strength;

          // Disturb gull state
          if (g.state === 'landed') {
            g.state = 'takingoff'; g.stateTimer = 0;
            g.vy = -2.5 - Math.abs(vn) * 0.1;
            g.vx = g.dir * (1.0 + Math.abs(vn) * 0.08);
          } else if (g.state === 'landing' || g.state === 'gliding') {
            g.state = 'flying'; g.stateTimer = 0;
          }

          // Damage
          g.health -= Math.abs(vn) * part.dmg * 8.572;
          if (g.health <= 0) {
            g.health = 0;
            g.state  = 'dead';
            g.stateTimer = 0;
            // Seed angular velocity (clamped); ragdoll init will distribute it
            const MAX_AV = Math.PI * 2 / 180;  // 1 rotation per 3 s at 60 fps
            g.angularVel = Math.max(-MAX_AV, Math.min(MAX_AV,
              (nx * b.vy - ny * b.vx) * 0.06 * g.dir));
            g.ragdoll = makeGullRagdoll(g);
            playGullDeath(g);
            // Two birds with one stone: the same ball's second kill and beyond.
            b.kills = (b.kills ?? 0) + 1;
            if (b.kills >= 2) {
              bursts.push({ x: g.x, y: g.y, count: b.kills, t: 0 });
              playMultiKillJingle(b.kills);
            }
          }
        }
        break; // one part hit per gull per ball per tick
      }
    }

    // ── Rest ──────────────────────────────────────────────────────────────
    if (b.grounded && Math.abs(b.vx) < REST_EPSILON && Math.abs(b.vy) < REST_EPSILON) {
      b.restFrames = (b.restFrames ?? 0) + 1;
      if (b.restFrames >= REST_FRAMES) {
        b.vx = 0; b.vy = 0;
        b.resting = true;
        stopBallRoll(b);
        continue;
      }
    } else {
      b.restFrames = 0;
    }

    // ── Rolling audio ─────────────────────────────────────────────────────
    if (b.grounded) {
      if (!b.rollAudio) startBallRoll(b, b.onBox ? 'box' : 'ground');
      tickBallRoll(b, Math.abs(b.vx));
    } else if (prevGrounded) {
      stopBallRoll(b);
    }

    // ── Cull ──────────────────────────────────────────────────────────────
    if (b.x < terrainStartX || b.x > terrainEndX()) { stopBallRoll(b); balls.splice(i, 1); continue; }
    if (b.age > 3600) { stopBallRoll(b); balls.splice(i, 1); continue; } // ~1 minute at 60 fps
  }
}

// ─── Collision ────────────────────────────────────────────────────────────────
function circleOverlapsBox(cx: number, cy: number, r: number, bx: number, by: number, bw: number, bh: number): boolean {
  const nearX = Math.max(bx, Math.min(cx, bx + bw));
  const nearY = Math.max(by, Math.min(cy, by + bh));
  const dx = cx - nearX, dy = cy - nearY;
  return dx * dx + dy * dy < r * r;
}

// Steering while the balloons carry the player. MOVE_SPEED and ACCEL are what
// a player gets with their feet planted; these are what is left without them.
// How far the player must climb from wherever the balloons lifted them before
// the run is won. Fixed in world units rather than screen heights: the ascent
// is then the same work on every window size, and cannot be shortened by
// drifting out over ground that falls away.
const WIN_CLIMB: number = 450;   // nine player-heights, about 18 s of buoyancy

const FLOAT_DRIFT: number = MOVE_SPEED * 0.15;   // 0.9 px/tick — a drift, not a stride
const FLOAT_ACCEL: number = ACCEL * 0.12;        // slow to build, slow to change
const FLOAT_DRAG: number  = 0.94;                // sheds the speed carried off the ground

// Animations advance with the simulation rather than with the frames drawn, so
// they run at the same speed on a 120 Hz screen as on a 60 Hz one and hold
// still while the game is paused. Counting frames ran them twice as fast on a
// fast screen and kept them moving under the Paused box.
function advanceAnimations(): void {
  for (let i: number = bursts.length - 1; i >= 0; i--) {
    if (++at(bursts, i).t > BURST_LIFE) bursts.splice(i, 1);
  }
  for (let i: number = pops.length - 1; i >= 0; i--) {
    if (++at(pops, i).t > POP_LIFE) pops.splice(i, 1);
  }
  meterFill  += (Math.min(playerBalloons.length / METER_SECTIONS, 1) - meterFill) * 0.10;
  meterPulse *= 0.9725;   // the pickup flash fades over about a second
  // The sling swaps hands to keep the cradle on the side it is aiming toward.
  const flipTarget = Math.cos(solvedAimAngle) >= 0 ? 1 : -1;
  slingshotFlip += (flipTarget - slingshotFlip) * 0.056;
}

// ─── Update ───────────────────────────────────────────────────────────────────
function update(): void {
  windTick++;
  windNoise += (Math.random() - 0.5) * 0.04;
  windNoise *= 0.995;

  maintainTerrain();
  cullObjects();
  updateBalloons();
  updateGulls();
  updateBalls();
  advanceAnimations();

  // Slingshot: drawn fully while held; releaseSlingshot drops it to zero.
  slingshotPull = mouseDown ? 1 : 0;

  const wasOnGround = onGround;
  onGround = false;
  // Contact from last tick decides how much steering the player gets this one.
  const wasTouchingGround = touchingGround;
  touchingGround = false;
  // A full bunch does not lift straight away: it holds for FLOAT_DELAY ticks
  // first. Losing a balloon in the meantime puts the whole wait back, so the
  // beat belongs to the tenth balloon rather than to the first nine.
  const hasFullBunch = playerBalloons.length >= 10;
  if (!hasFullBunch) floatDelay = FLOAT_DELAY;
  else if (floatDelay > 0) floatDelay--;
  const isFloating = hasFullBunch && floatDelay === 0;

  // Re-taken every time the balloons take the player's weight, so height can
  // never be banked by losing the bunch and collecting it again further uphill.
  if (!isFloating) floatAnchorY = null;
  else floatAnchorY ??= player.y;

  // Ski mode: S / ArrowDown held — disables horizontal input on the ground and
  // in a jump. It does not reach a player hanging from the balloons: with
  // nothing underfoot there is nothing to ski on, so the float rule below comes
  // first and sheds the carried speed whether or not the key is held.
  const skiKeyHeld = held('KeyS') || held('ArrowDown');
  const wasSkiing  = isSkiing;
  isSkiing = skiKeyHeld && wasOnGround;

  if (isFloating && !wasTouchingGround) {
    // Hanging from ten balloons with nothing underfoot. There is nothing to
    // push against, so steering collapses to a slow drift and whatever speed
    // was carried up off the ground bleeds away. Note the inner Math.min /
    // Math.max: a fast carry-over is left for the drag to shed rather than
    // being yanked down to the drift cap in a single tick.
    player.vx *= FLOAT_DRAG;
    if (held('ArrowLeft')  || held('KeyA'))
      player.vx = Math.max(player.vx - FLOAT_ACCEL, Math.min(player.vx, -FLOAT_DRIFT));
    else if (held('ArrowRight') || held('KeyD'))
      player.vx = Math.min(player.vx + FLOAT_ACCEL, Math.max(player.vx,  FLOAT_DRIFT));

    if (wasSkiing) fadeOutSkiAudio();
  } else if (skiKeyHeld) {
    if (isSkiing) {
      // On ground: slope gravity + nearly frictionless drag, no steering
      const slope = (terrainY(player.x + 1) - terrainY(player.x - 1)) / 2;
      const len   = Math.sqrt(1 + slope * slope);
      player.vx  += GRAVITY * slope / len;
      player.vx  *= SKI_DRAG;

      if (!wasSkiing) startSkiAudio();
      tickSkiAudio(Math.abs(player.vx));
    } else {
      // In a jump with the ski key held: carry momentum, no control
      if (wasSkiing) fadeOutSkiAudio();
    }
  } else {
    // Normal friction and directional input. A floating player whose feet are
    // back on the dirt gets this too, so a slope can still be run up.
    if (held('ArrowLeft')  || held('KeyA'))      player.vx = Math.max(player.vx - ACCEL, -MOVE_SPEED);
    else if (held('ArrowRight') || held('KeyD')) player.vx = Math.min(player.vx + ACCEL,  MOVE_SPEED);
    else {
      if (player.vx > 0)      player.vx = Math.max(0, player.vx - FRICTION);
      else if (player.vx < 0) player.vx = Math.min(0, player.vx + FRICTION);
    }

    if (wasSkiing) fadeOutSkiAudio();
  }

  if (wasOnGround) {
    const slope = (terrainY(player.x + 1) - terrainY(player.x - 1)) / 2;
    const len = Math.sqrt(1 + slope * slope);
    player.x += player.vx / len;
    const slopeMoveDy = player.vx * slope / len;
    player.y += slopeMoveDy;
  } else {
    player.x += player.vx;
  }

  // Vertical movement
  const jumpHeld = held('ArrowUp') || held('KeyW') || held('Space');
  if (isFloating) {
    // Buoyancy: drift upward at 25 px/s (~0.417 px/tick), ignore jump/gravity
    const FLOAT_VY = -25 / 60;
    player.vy += (FLOAT_VY - player.vy) * 0.04;
  } else {
    player.vy += GRAVITY + (player.vy < 0 && !jumpHeld ? 0.6 : 0);
  }
  player.y += player.vy;
  const fallVy = player.vy;   // downward speed at any touchdown this tick

  // Terrain collision. The ground is solid while floating too: a balloon drift
  // into a hillside rides up the slope instead of sinking through it. Only a
  // player who is not floating counts as standing on it, so a drift along the
  // dirt does not hand back jumping, skiing or footsteps.
  const groundAtPlayer = terrainY(player.x);
  if (player.y + player.radius >= groundAtPlayer) {
    player.y = groundAtPlayer - player.radius;
    if (player.vy > 0) player.vy = 0;
    touchingGround = true;
    if (!isFloating) {
      onGround = true;
      groundSurface = 'grass';
    }
  }

  // Box collision. One pass resolves any overlap by pushing the player's circle
  // out along the nearest box surface, so a corner poking out of a slope is
  // handled the same as a flat side or top: the push is consistent every frame,
  // which is what stops the old in-out bouncing when walking into a half-buried
  // box. Floating players are pushed out the same way — the crates stay solid —
  // but the top face is ground only to a player on foot.
  for (const box of boxes) {
    const boxGroundY = Math.max(terrainY(box.x), terrainY(box.x + BOX_SIZE));
    const boxY = boxGroundY - BOX_SIZE;
    const r = player.radius;
    const nearX = Math.max(box.x, Math.min(player.x, box.x + BOX_SIZE));
    const nearY = Math.max(boxY,  Math.min(player.y, boxY + BOX_SIZE));
    const dx = player.x - nearX, dy = player.y - nearY;
    const d2 = dx * dx + dy * dy;
    if (d2 >= r * r) continue;

    let nx: number, ny: number, push: number;
    if (d2 > 0) {
      const d = Math.sqrt(d2);
      nx = dx / d; ny = dy / d; push = r - d;
    } else {
      // Centre inside the box: leave by the thinnest wall.
      const toLeft = player.x - box.x, toRight = box.x + BOX_SIZE - player.x;
      const toTop  = player.y - boxY,  toBottom = boxY + BOX_SIZE - player.y;
      const m = Math.min(toLeft, toRight, toTop, toBottom);
      nx = m === toLeft ? -1 : m === toRight ? 1 : 0;
      ny = m === toTop  ? -1 : m === toBottom ? 1 : 0;
      push = m + r;
    }
    player.x += nx * push;
    player.y += ny * push;

    if (ny < -0.5) {
      // Standing on the top face.
      if (player.vy > 0) player.vy = 0;
      touchingGround = true;
      if (!isFloating) {
        onGround = true;
        groundSurface = 'box';
      }
    } else if (ny > 0.5) {
      // Bumped the underside. Buoyancy is left alone so a floating player can
      // never be pinned under a crate; the push-out is what keeps them clear.
      if (player.vy < 0 && !isFloating) player.vy = 0;
    } else {
      // A side: stop motion into it, keep motion away from it.
      if ((nx > 0 && player.vx < 0) || (nx < 0 && player.vx > 0)) player.vx = 0;
    }
  }

  // A box push moves the player sideways, which can leave them a shade under
  // terrain that is higher at their new x. Re-seat them on the dirt: it is the
  // floor, and lifting can never drive them further into a crate, whose sides
  // are vertical and whose top they would already be standing above.
  const groundAfterBoxes = terrainY(player.x);
  if (player.y + player.radius > groundAfterBoxes) {
    player.y = groundAfterBoxes - player.radius;
    if (player.vy > 0) player.vy = 0;
    touchingGround = true;
    if (!isFloating) {
      onGround = true;
      groundSurface = 'grass';
    }
  }

  // ── Landing and footsteps ─────────────────────────────────────────────
  const STEP_DISTANCE = 68;   // px of travel between footsteps
  if (onGround && !wasOnGround) {
    if (fallVy > 1.5) playLand(groundSurface, fallVy);
    stepAccum = STEP_DISTANCE * 0.5;   // first step comes soon after touchdown
  }
  if (onGround && !isSkiing && Math.abs(player.vx) > 0.6) {
    stepAccum += Math.abs(player.vx);
    if (stepAccum >= STEP_DISTANCE) {
      stepAccum -= STEP_DISTANCE;
      playFootstep(groundSurface, Math.min(1, Math.abs(player.vx) / MOVE_SPEED));
    }
  } else if (!onGround) {
    stepAccum = 0;
  }

  // One camera, whether the player is walking or hanging from ten balloons.
  // Floating used to pin the view to the winning altitude instead, which meant
  // the moment the balloons took over the picture lurched by a few hundred
  // pixels. The goal is an altitude the player has to reach, not somewhere the
  // camera has to be.
  camera.x = player.x - VIEW_W / 2;
  const BUFFER = 120;
  const targetCameraY = player.y - VIEW_H * CAMERA_BIAS;
  camera.y += (targetCameraY - camera.y) * 0.08;
  if (player.y - camera.y < BUFFER)           camera.y = player.y - BUFFER;
  if (player.y - camera.y > VIEW_H - BUFFER)  camera.y = player.y - (VIEW_H - BUFFER);

  // The ascent is measured from the altitude at which the balloons took the
  // player's weight, so drifting out over falling ground cannot shorten it.
  if (isFloating && floatAnchorY !== null && player.y <= floatAnchorY - WIN_CLIMB) winMatch();

  advancePopulation();
}

// ─── Aim solver ───────────────────────────────────────────────────────────────
// Where a shot leaves from: the sling's cradle, drawn back along the aim (14 px
// behind the player's centre at full pull) and a couple of pixels to the side
// the sling is held on. The solver flies its trial shots from here too: from
// the player's centre, 61 of 165 predicted hits on a survey grid landed more
// than the hit radius wide of the target, the worst by 45 px.
function launchPoint(angle: number, pull: number): Vec {
  const cradleFwd = 4 - pull * 18;
  return {
    x: player.x + Math.cos(angle) * cradleFwd - Math.sin(angle) * 2 * slingshotFlip,
    y: player.y + Math.sin(angle) * cradleFwd + Math.cos(angle) * 2 * slingshotFlip,
  };
}

// Simulate one ball trajectory; report the closest approach to (tx, ty): how
// near it came, on which tick, and whether the ball was still climbing when it
// got there. Screen y grows downward, so climbing is a negative vy.
// How close a simulated shot came to its target, and in what manner.
interface Approach {
  dist: number;        // closest approach to the target
  tick: number;        // when that closest approach came
  climbing: boolean;   // still rising at that moment
}

function simTrajectory(angle: number, pull: number, tx: number, ty: number): Approach {
  let vx: number = Math.cos(angle) * BALL_SPEED * pull + player.vx;
  let vy: number = Math.sin(angle) * BALL_SPEED * pull + player.vy;
  const at = launchPoint(angle, pull);
  let bx: number = at.x, by: number = at.y;
  let best2: number = Infinity, bestTick: number = 0, bestVy: number = 0, rising: number = 0;
  for (let i: number = 0; i < 360; i++) {
    vy += GRAVITY; vx *= BALL_DRAG; vy *= BALL_DRAG;
    bx += vx; by += vy;
    const d2 = (bx - tx) ** 2 + (by - ty) ** 2;
    if (d2 < best2) { best2 = d2; bestTick = i + 1; bestVy = vy; rising = 0; }
    else if (++rising > 80) break;   // past closest point — no need to continue
    if (by > terrainY(bx)) break;   // hit ground
  }
  return { dist: Math.sqrt(best2), tick: bestTick, climbing: bestVy < 0 };
}

// Thin wrapper returning just the distance (used by refineAngle).
function simMinDist(angle: number, pull: number, tx: number, ty: number): number {
  return simTrajectory(angle, pull, tx, ty).dist;
}

// A candidate shot, as the solver scores it.
interface Shot extends Approach {
  angle: number;
  hit: boolean;        // the approach is within the hit radius
}

// Which of two candidate shots to keep. Reaching the target beats not reaching
// it. Among those that reach, one still climbing at the target beats one
// dropping onto it: the arc then peaks beyond the target instead of short of
// it, so the ball is still gaining ground when it arrives rather than lobbing
// down. Between two otherwise alike, the one that gets there sooner.
function shotBeats(a: Shot, b: Shot): boolean {
  if (a.hit !== b.hit) return a.hit;
  if (!a.hit) return a.dist < b.dist;
  if (a.climbing !== b.climbing) return a.climbing;
  return a.tick < b.tick;
}

// The bracket handed to refineAngle starts 30° wide and shrinks by about 0.618
// a pass, so eighteen leaves it under a fortieth of a degree — far finer than
// the ten-pixel radius that counts as a hit, and it pays for the climb search.
const AIM_REFINE_STEPS: number = 18;

// Golden-section minimiser of simMinDist over a single angular interval [lo, hi].
function refineAngle(lo: number, hi: number, pull: number, tx: number, ty: number): number {
  const gr = (Math.sqrt(5) - 1) / 2;
  for (let i: number = 0; i < AIM_REFINE_STEPS; i++) {
    const m1 = hi - gr * (hi - lo), m2 = lo + gr * (hi - lo);
    if (simMinDist(m1, pull, tx, ty) < simMinDist(m2, pull, tx, ty)) hi = m2;
    else lo = m1;
  }
  return (lo + hi) / 2;
}

// The two arcs that reach a target above the player differ in which crossing
// of the target's altitude lands on it: the flatter arc passes the target on
// the way up, its apex still ahead, while the steeper one passes it on the way
// down. Rather than finding the closest approach anywhere along the flight and
// then asking which half of the window it fell in, this measures the closest
// approach over the climb alone — the flight up to the apex and no further. It
// is the same distance the general solver scores, so a value inside the hit
// radius means exactly what a hit means elsewhere, and it is only ever taken
// while the ball is still rising, so whatever minimises it is climbing at the
// target by construction.
function ascendingMiss(angle: number, pull: number, tx: number, ty: number): number {
  let vx: number = Math.cos(angle) * BALL_SPEED * pull + player.vx;
  let vy: number = Math.sin(angle) * BALL_SPEED * pull + player.vy;
  const at = launchPoint(angle, pull);
  let bx: number = at.x, by: number = at.y;
  let best2: number = (bx - tx) ** 2 + (by - ty) ** 2;
  for (let i: number = 0; i < 360; i++) {
    vy += GRAVITY; vx *= BALL_DRAG; vy *= BALL_DRAG;
    bx += vx; by += vy;
    if (vy >= 0) break;             // over the top; the rest is descent
    const d2 = (bx - tx) ** 2 + (by - ty) ** 2;
    if (d2 < best2) best2 = d2;
    if (by > terrainY(bx)) break;
  }
  return Math.sqrt(best2);
}

// Golden-section minimiser of ascendingMiss over [lo, hi].
function refineAscending(lo: number, hi: number, pull: number, tx: number, ty: number): number {
  const gr = (Math.sqrt(5) - 1) / 2;
  for (let i: number = 0; i < AIM_ASCENT_STEPS; i++) {
    const m1 = hi - gr * (hi - lo), m2 = lo + gr * (hi - lo);
    if (ascendingMiss(m1, pull, tx, ty) < ascendingMiss(m2, pull, tx, ty)) hi = m2;
    else lo = m1;
  }
  return (lo + hi) / 2;
}

// The climb search samples its bracket first and refines around the best
// sample, rather than trusting the objective to be one smooth valley. The
// flight is integrated in whole ticks, which puts a fine ripple on any measure
// taken from it; a search seeded from the lowest sample rides over the ripple
// where a search from the whole bracket could settle in a bump. Sixteen passes
// over a bracket two samples wide bring it to a few thousandths of a degree.
const AIM_ASCENT_SAMPLES: number = 32;
const AIM_ASCENT_STEPS: number   = 16;

// The launch angle that meets the target while still climbing, or null if no
// angle does at this pull. The bracket runs from a little below horizontal
// towards the target to a little past straight up — the overlap past vertical
// is for a player moving fast the other way, whose throw needs the extra lean.
function solveClimbing(pull: number, tx: number, ty: number): number | null {
  const dx = tx - player.x;
  const lean = 0.26;   // ~15° past vertical
  let lo: number, hi: number;
  if (Math.abs(dx) < 1)  { lo = -Math.PI / 2 - 0.35; hi = -Math.PI / 2 + 0.35; }
  else if (dx > 0)       { lo = -Math.PI / 2 - lean; hi = 0.2; }
  else                   { lo = -Math.PI - 0.2;      hi = -Math.PI / 2 + lean; }

  const step = (hi - lo) / AIM_ASCENT_SAMPLES;
  let bestI: number = 0, bestMiss: number = Infinity;
  for (let i: number = 0; i <= AIM_ASCENT_SAMPLES; i++) {
    const miss = ascendingMiss(lo + step * i, pull, tx, ty);
    if (miss < bestMiss) { bestMiss = miss; bestI = i; }
  }
  const angle = refineAscending(
    Math.max(lo, lo + step * (bestI - 1)),
    Math.min(hi, lo + step * (bestI + 1)),
    pull, tx, ty,
  );
  return ascendingMiss(angle, pull, tx, ty) <= BALL_RADIUS * 2 ? angle : null;
}

// How many of the coarse scan's valleys are worth refining. Three covers the
// direct shot, the lofted one and a backwards oddity; refining more costs a
// golden-section search each and buys nothing.
const MAX_AIM_BASINS: number = 3;

// Main solver: returns the launch angle (radians) whose trajectory passes closest
// to (tx, ty). If that target is unreachable, steps back along the player→target
// ray to the furthest reachable point and solves for that instead.
function computeSolvedAimAngle(pull = slingshotPull): number {
  const tx = camera.x + mouse.x, ty = camera.y + mouse.y;
  const rawAngle = Math.atan2(ty - player.y, tx - player.x);
  if (pull < 0.05) return rawAngle;

  // ── A shot that is still climbing at the target, if one exists ───────────
  const climbing = solveClimbing(pull, tx, ty);
  if (climbing !== null) return climbing;

  // ── Otherwise the closest approach anywhere along the flight ─────────────
  // Reached only when no climbing shot exists: the target is level with or
  // below the player, or too far for this pull. The scan below then finds the
  // best of the arcs that drop onto it, or the nearest miss.

  // ── Coarse scan: 24 evenly-spaced angles ──────────────────────────────────
  const N    = 24;
  const step = (2 * Math.PI) / N;
  const dists: number[] = [];
  for (let i: number = 0; i < N; i++) dists.push(simMinDist(-Math.PI + step * i, pull, tx, ty));

  // Every local minimum of that scan is a candidate. The direct shot and the
  // lofted one are separate valleys, and taking simply the two smallest samples
  // used to pick two neighbours out of the same valley — which left the other
  // branch, often the climbing one, never examined at all.
  const basins: number[] = [];
  for (const [i, dist] of dists.entries()) {
    const prev = at(dists, (i + N - 1) % N), next = at(dists, (i + 1) % N);
    if (dist < prev && dist <= next) basins.push(i);
  }
  // A perfectly flat scan has no local minimum; fall back to its lowest sample.
  if (basins.length === 0) basins.push(dists.indexOf(Math.min(...dists)));
  basins.sort((a, b) => at(dists, a) - at(dists, b));

  // ── Refine the most promising basins and keep the best shot ──────────────
  let best: Shot | null = null;
  for (const i of basins.slice(0, MAX_AIM_BASINS)) {
    const angle = refineAngle(-Math.PI + step * i - step, -Math.PI + step * i + step,
                              pull, tx, ty);
    const { dist, tick, climbing } = simTrajectory(angle, pull, tx, ty);
    const shot: Shot = { angle, dist, tick, climbing, hit: dist <= BALL_RADIUS * 2 };
    if (!best || shotBeats(shot, best)) best = shot;
  }

  const bestAngle = best ? best.angle : rawAngle;
  const bestDist  = best ? best.dist  : Infinity;
  if (bestDist <= BALL_RADIUS * 2) return bestAngle;   // target reachable ✓

  // ── Out of range: binary-search for furthest reachable point on the ray ──
  const rayDX = tx - player.x, rayDY = ty - player.y;
  const rayLen = Math.sqrt(rayDX * rayDX + rayDY * rayDY) || 1;
  const nx = rayDX / rayLen, ny = rayDY / rayLen;

  const canReach = (r: number): boolean => {
    const rx = player.x + nx * r, ry = player.y + ny * r;
    for (let i: number = 0; i < N; i++) {
      if (simMinDist(-Math.PI + step * i, pull, rx, ry) <= BALL_RADIUS * 3)
        return true;
    }
    return false;
  };

  // If nothing reachable at all along the ray, return best from initial solve
  if (!canReach(20)) return bestAngle;

  let rLo: number = 20, rHi: number = rayLen;
  for (let i: number = 0; i < 12; i++) {
    const mid = (rLo + rHi) / 2;
    if (canReach(mid)) rLo = mid; else rHi = mid;
  }

  // Solve for the clamped target on the ray
  const rtx = player.x + nx * rLo, rty = player.y + ny * rLo;
  let rBestAngle: number = rawAngle, rBestDist: number = Infinity;
  for (let i: number = 0; i < N; i++) {
    const angle = -Math.PI + step * i;
    const d = simMinDist(angle, pull, rtx, rty);
    if (d < rBestDist) { rBestDist = d; rBestAngle = angle; }
  }
  return refineAngle(rBestAngle - step, rBestAngle + step, pull, rtx, rty);
}

// ─── Slingshot ────────────────────────────────────────────────────────────────
function drawSlingshot(px: number, py: number, angle: number, pull = 0, flip = 1): void {
  const ca = Math.cos(angle), sa = Math.sin(angle);   // unit vec toward mouse
  const cp = -sa,             sp = ca;                 // perpendicular (CCW 90°)

  // pt(fwd, side) → canvas point; flip mirrors the side axis for left-hand hold
  const pt = (f: number, s: number): Point => [px + ca * f + cp * s * flip, py + sa * f + sp * s * flip];

  // ── Key geometry ────────────────────────────────────────────────────────────
  const handleEnd = pt(-11,  2);   // butt of the handle stick
  const forkBase  = pt( 20,  2);   // where the Y-fork splits from the shaft
  const tipL      = pt( 34, 12);   // upper prong tip
  const tipR      = pt( 34, -8);   // lower prong tip (slight asymmetry looks natural)
  const cradle    = pt(4 - pull * 18,  2);  // centred between arms, pulls back along aim
  const gripPos   = pt( 17,  5);   // grip-hand circle centre (holds the fork shaft)
  const drawPos   = cradle;        // draw-hand circle centre (holds the cradle)

  // ── Wood colours ────────────────────────────────────────────────────────────
  const woodDk = '#6b4423';
  const woodMd = '#8b5e34';
  const woodLt = '#b07d50';

  ctx.lineCap  = 'round';
  ctx.lineJoin = 'round';

  // Helper: draw a wooden rod with a highlight streak
  function woodRod(ax: number, ay: number, bx: number, by: number, thick: number): void {
    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
    ctx.strokeStyle = woodDk; ctx.lineWidth = thick + 2; ctx.stroke();

    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
    ctx.strokeStyle = woodMd; ctx.lineWidth = thick; ctx.stroke();

    // Thin lighter highlight along the top edge
    const off = thick * 0.28;
    const nx = cp * off * flip, ny = sp * off * flip;
    ctx.beginPath();
    ctx.moveTo(ax + nx, ay + ny); ctx.lineTo(bx + nx, by + ny);
    ctx.strokeStyle = woodLt; ctx.lineWidth = 1.2; ctx.stroke();
  }

  // Handle shaft
  woodRod(handleEnd[0], handleEnd[1], forkBase[0], forkBase[1], 5);

  // Prongs
  woodRod(forkBase[0], forkBase[1], tipL[0], tipL[1], 4);
  woodRod(forkBase[0], forkBase[1], tipR[0], tipR[1], 4);

  // Small notch caps at prong tips
  ctx.fillStyle = woodDk;
  for (const [tx, ty] of [tipL, tipR]) {
    ctx.beginPath(); ctx.arc(tx, ty, 3, 0, Math.PI * 2); ctx.fill();
  }

  // ── Rubber bands ────────────────────────────────────────────────────────────
  // Bands bow slightly outward from each tip (tension look)
  function rubberBand(tx: number, ty: number, bow: number): void {
    const mx = (tx + cradle[0]) * 0.5 + cp * bow * flip;
    const my = (ty + cradle[1]) * 0.5 + sp * bow * flip;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.quadraticCurveTo(mx, my, cradle[0], cradle[1]);
    ctx.strokeStyle = '#b87820';
    ctx.lineWidth = 2.2;
    ctx.stroke();
    // Darker shadow underneath
    ctx.strokeStyle = '#7a4e0a';
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
  rubberBand(tipL[0], tipL[1],  5 * (1 - pull));
  rubberBand(tipR[0], tipR[1], -5 * (1 - pull));

  // Small leather pouch at cradle
  ctx.beginPath();
  ctx.ellipse(cradle[0], cradle[1], 4, 3, angle, 0, Math.PI * 2);
  ctx.fillStyle = '#7a5030';
  ctx.fill();

  // ── Hands ───────────────────────────────────────────────────────────────────
  const skinFill   = '#f0b870';
  const skinStroke = '#b07840';

  function hand(pos: Point, r: number): void {
    ctx.beginPath(); ctx.arc(pos[0], pos[1], r, 0, Math.PI * 2);
    ctx.fillStyle = skinFill; ctx.fill();
    ctx.strokeStyle = skinStroke; ctx.lineWidth = 1.2; ctx.stroke();
  }

  hand(drawPos, 4.5);   // draw hand (holds cradle) — drawn first so grip is on top
  hand(gripPos, 5);     // grip hand (holds fork shaft)
}

// ─── Draw ─────────────────────────────────────────────────────────────────────
function draw(): void {
  ctx.save();
  ctx.translate(-camera.x, -camera.y);

  const step   = 4;
  const left   = camera.x;
  const right  = camera.x + VIEW_W;
  const bottom = camera.y + VIEW_H;

  // Terrain fill
  ctx.beginPath();
  ctx.moveTo(left, bottom);
  for (let x: number = left; x <= right; x += step) ctx.lineTo(x, terrainY(x));
  ctx.lineTo(right, bottom);
  ctx.closePath();
  ctx.fillStyle = '#2d7a2d';
  ctx.fill();

  // Palm trees
  for (const tree of trees) drawPalmTree(tree, terrainY(tree.x));

  // Boxes
  ctx.fillStyle = '#7a4a1e';
  ctx.strokeStyle = '#5a3410';
  ctx.lineWidth = 2;
  for (const box of boxes) {
    const boxGroundY = Math.max(terrainY(box.x), terrainY(box.x + BOX_SIZE));
    const boxY = boxGroundY - BOX_SIZE;
    ctx.fillRect(box.x, boxY, BOX_SIZE, BOX_SIZE);
    ctx.strokeRect(box.x, boxY, BOX_SIZE, BOX_SIZE);
  }

  // Seagulls (drawn in world space, after ground objects so they appear in front)
  for (const g of gulls) drawGull(g);

  // Slingshot balls (world space — ctx already translated by -camera)
  ctx.fillStyle   = getBallGradient();
  ctx.strokeStyle = '#555';
  ctx.lineWidth   = 0.6;
  for (const b of balls) {
    ctx.save();
    ctx.translate(b.x, b.y);
    ctx.beginPath();
    ctx.arc(0, 0, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // Multi-kill comic bursts (world space): a ring of radiating strokes that
  // pops out, holds a beat, and shrinks away, with the stone's kill count in
  // the middle. advanceAnimations ages them.
  for (const burst of bursts) {
    const frac = burst.t / BURST_LIFE;
    // Pop in fast (overshoot), hold for most of the life, then shrink out.
    // The pop-in and shrink-out are fixed in ticks so only the hold grows.
    const inEnd = 9 / BURST_LIFE, outStart = 1 - 14 / BURST_LIFE;
    const scale = frac < inEnd    ? 1.15 * Math.sin((frac / inEnd) * Math.PI / 2)
                : frac < outStart ? 1.15 - 0.15 * ((frac - inEnd) / (outStart - inEnd))
                :                   1 - (frac - outStart) / (1 - outStart);
    const alpha = frac < outStart ? 1 : 1 - (frac - outStart) / (1 - outStart);
    const R0 = 14 * scale, R1 = 30 * scale;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.lineCap = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#ffd633';
    ctx.beginPath();
    for (let k: number = 0; k < 12; k++) {
      const a = (k / 12) * Math.PI * 2 + (k % 2 ? 0.12 : 0);
      const r1 = k % 2 ? R1 * 0.75 : R1;
      ctx.moveTo(burst.x + Math.cos(a) * R0, burst.y + Math.sin(a) * R0);
      ctx.lineTo(burst.x + Math.cos(a) * r1, burst.y + Math.sin(a) * r1);
    }
    ctx.stroke();
    ctx.font = `bold ${Math.round(16 * scale)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillText(String(burst.count), burst.x + 1, burst.y + 1);
    ctx.fillStyle = '#ffd633';
    ctx.fillText(String(burst.count), burst.x, burst.y);
    ctx.restore();
  }

  // Balloon pop animations (world space)
  for (const pop of pops) {
    const frac  = pop.t / POP_LIFE;    // 0→1
    const alpha = 1 - frac;
    // Three expanding rings
    for (let ring: number = 1; ring <= 3; ring++) {
      const r = BALLOON_RADIUS * (1 + ring * frac * 2.5);
      ctx.beginPath();
      ctx.arc(pop.x, pop.y, r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(224,32,32,${alpha * (1 - ring * 0.25)})`;
      ctx.lineWidth   = 2.5 * (1 - frac);
      ctx.stroke();
    }
    // Central flash
    ctx.beginPath();
    ctx.arc(pop.x, pop.y, BALLOON_RADIUS * (1 - frac * 0.8), 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,180,80,${alpha * 0.7})`;
    ctx.fill();
  }

  ctx.restore();

  // Player
  const screenX = VIEW_W / 2;
  const screenY = player.y - camera.y;
  ctx.beginPath(); ctx.arc(screenX, screenY, player.radius, 0, Math.PI * 2);
  ctx.fillStyle = '#4488ff'; ctx.fill();
  ctx.beginPath(); ctx.arc(screenX, screenY, player.radius, 0, Math.PI * 2);
  ctx.strokeStyle = '#ffdd00'; ctx.lineWidth = 5; ctx.stroke();

  // Slingshot — orbits around player. While held the draw is full, so this
  // solves the full-power arc; idle it just points at the cursor, which keeps
  // the solver off the frame budget until it is needed.
  solvedAimAngle = computeSolvedAimAngle();
  drawSlingshot(screenX, screenY, solvedAimAngle, slingshotPull, slingshotFlip);

  // Player balloons (rope segments in world space → convert to screen)
  for (const b of playerBalloons) {
    // Rope string
    ctx.beginPath();
    for (const [i, node] of b.nodes.entries()) {
      if (i === 0) ctx.moveTo(node.x - camera.x, node.y - camera.y);
      else         ctx.lineTo(node.x - camera.x, node.y - camera.y);
    }
    ctx.strokeStyle = '#bbb';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Balloon at tip
    const last = tipOf(b);
    const bx = last.x - camera.x;
    const by = last.y - camera.y;
    ctx.beginPath(); ctx.arc(bx, by, BALLOON_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = '#e02020'; ctx.fill();
    ctx.beginPath(); ctx.arc(bx - 3, by - 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.fill();
    ctx.beginPath(); ctx.arc(bx, by + BALLOON_RADIUS, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#a01010'; ctx.fill();
  }
  beginUI();

  // The count, its meter and the objective line sit together at the foot of
  // the screen, over the grass, leaving the sky clear — that is where the
  // balloons rise and the gulls circle, and where the player is looking.
  const METER_W   = 220;
  const METER_H   = 16;
  const METER_R   = METER_H / 2;
  const inset     = uiInsets();   // a notch or home indicator to keep clear of
  const mx        = uiW() / 2 - METER_W / 2;
  // Bottom-centre on a desktop. A thumb pad owns the bottom of a phone, and on
  // one held upright it is wide enough to reach the middle, so there the meter
  // goes to the top and shares a line with the clock.
  const my        = touchMode ? 46 + inset.top : uiH() - 70 - inset.bottom;

  // Balloon counter text, riding just above the meter
  const count = playerBalloons.length;
  const label = count === 1 ? '1 balloon' : `${count} balloons`;
  ctx.font = 'bold 18px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillText(label, uiW() / 2, my - 10);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, uiW() / 2, my - 12);
  ctx.textAlign = 'left';

  // Match clock. Bottom-left on a desktop; that corner belongs to the touch
  // pad, so on a touch device it moves to the top, alongside the meter.
  const clock = formatTime(matchTimeMs());
  const clockX = 16 + inset.left;
  const clockY = touchMode ? 34 + inset.top : uiH() - 20 - inset.bottom;
  ctx.font = 'bold 20px monospace';
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillText(clock, clockX + 2, clockY + 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(clock, clockX, clockY);

  // Helper: rounded-rect path
  function capsulePath(x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  // Outer glow burst on pickup (drawn before the capsule, no clip)
  if (meterPulse > 0.01) {
    for (let ring: number = 3; ring >= 1; ring--) {
      const spread = ring * 35 * meterPulse;
      capsulePath(mx - spread, my - spread, METER_W + spread * 2, METER_H + spread * 2, METER_R + spread);
      ctx.fillStyle = `rgba(255, 60, 60, ${meterPulse * 0.22})`;
      ctx.fill();
    }
  }

  ctx.save();

  // Dark glass background
  capsulePath(mx, my, METER_W, METER_H, METER_R);
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  ctx.fill();

  // Clip everything inside capsule
  capsulePath(mx, my, METER_W, METER_H, METER_R);
  ctx.clip();

  // Red fill
  if (meterFill > 0.001) {
    const fillW = meterFill * METER_W;
    const fillGrad = ctx.createLinearGradient(mx, my, mx, my + METER_H);
    fillGrad.addColorStop(0,   '#ff7070');
    fillGrad.addColorStop(0.45,'#e02020');
    fillGrad.addColorStop(1,   '#991010');
    ctx.fillStyle = fillGrad;
    ctx.fillRect(mx, my, fillW, METER_H);
  }

  // White flash overlay on pickup
  if (meterPulse > 0.01) {
    ctx.fillStyle = `rgba(255,255,255,${meterPulse * 0.55})`;
    ctx.fillRect(mx, my, METER_W, METER_H);
  }

  // Notch dividers (9 notches = 10 sections)
  ctx.strokeStyle = 'rgba(160,160,160,0.65)';
  ctx.lineWidth = 1.5;
  for (let i: number = 1; i < METER_SECTIONS; i++) {
    const nx = mx + (METER_W / METER_SECTIONS) * i;
    ctx.beginPath();
    ctx.moveTo(nx, my);
    ctx.lineTo(nx, my + METER_H);
    ctx.stroke();
  }

  // Top sheen (glassy highlight)
  const sheenGrad = ctx.createLinearGradient(mx, my, mx, my + METER_H * 0.55);
  sheenGrad.addColorStop(0, 'rgba(255,255,255,0.30)');
  sheenGrad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = sheenGrad;
  ctx.fillRect(mx, my, METER_W, METER_H);

  ctx.restore();

  // Glass border (outside clip so it's crisp)
  capsulePath(mx, my, METER_W, METER_H, METER_R);
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Inner shadow ring for depth
  capsulePath(mx + 1, my + 1, METER_W - 2, METER_H - 2, METER_R - 1);
  ctx.strokeStyle = 'rgba(0,0,0,0.18)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Objective line under the meter: gather until the meter fills, then
  // keep the gulls off the balloons on the way up.
  const objective = playerBalloons.length >= 10 ? 'Defend balloons' : 'Collect more balloons';
  ctx.font = '13px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillText(objective, uiW() / 2, my + METER_H + 19);
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText(objective, uiW() / 2, my + METER_H + 18);
  ctx.textAlign = 'left';

  drawControlsHelp();
  endUI();
}

// ─── Controls help ────────────────────────────────────────────────────────────
// A muted summary of the controls, anchored to the bottom-right corner so it
// stays out of the way of play. Each line reads function first, because the
// reader knows what they want to do and is looking for how. The keyboard
// lines then have a column for the letters, mouse and Space and one for the
// arrow keys, each padded to its widest entry, so the two ways of playing
// read as two schemes side by side; the letters lead because every function
// has one and the arrows cover only three, so the gaps fall at the ragged
// right edge. There are no slashes, the columns group.
function helpRows(rows: readonly (readonly string[])[]): string[] {
  const columns = Math.max(...rows.map(row => row.length));
  const widths = Array.from({ length: columns }, (_, col) => Math.max(...rows.map(row => (row[col] ?? '').length)));
  return rows.map(row => row.map((cell, col) => cell.padEnd(widths[col] ?? 0)).join('  ').trimEnd());
}

const CONTROLS_HELP: string[] = helpRows([
  ['move:', 'a d',      '← →'],
  ['jump:', 'w space',  '↑'],
  ['ski:',  's (hold)', '↓'],
  ['aim:',  'mouse',    ''],
  ['fire:', 'click',    ''],
]);

// On a touch device the pad speaks for itself, so only the slingshot needs
// explaining.
const TOUCH_HELP: string[] = helpRows([
  ['move:', 'pad'],
  ['fire:', 'tap'],
  ['aim:',  'hold first'],
]);

function drawControlsHelp(): void {
  const lines = touchMode ? TOUCH_HELP : CONTROLS_HELP;
  const size = 18;
  const lineHeight = size + 6;
  const margin = 16;
  const inset = uiInsets();
  ctx.save();
  ctx.font = `${size}px monospace`;
  // The block sits in the corner but its lines are left-aligned, so the
  // padded names and the keys each form a straight column.
  const blockWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
  const left = uiW() - margin - inset.right - blockWidth;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  lines.forEach((line, i) => {
    const y = uiH() - margin - inset.bottom - (lines.length - 1 - i) * lineHeight;
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillText(line, left + 2, y + 2);
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText(line, left, y);
  });
  ctx.restore();
}

// ─── Match timer ──────────────────────────────────────────────────────────────
// The clock runs from the click on Start until the player floats out of the
// world. Time spent paused does not count against the run.
let matchStartMs: number   = 0;     // performance.now() when the clock last started running
let matchElapsedMs: number = 0;     // time banked before the current running stretch
let finalTimeMs: number | null = null;  // frozen once the run is won
let lastRunMs:   number | null = null;  // the time the start screen reports back, null before the first run
let wonWithBest: boolean    = false; // the winning run took the top spot

function matchTimeMs(): number {
  if (finalTimeMs !== null) return finalTimeMs;
  if (!gameStarted) return 0;
  return matchElapsedMs + (paused ? 0 : performance.now() - matchStartMs);
}

function formatTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return mm + ':' + ss;
}

function startMatch(): void {
  gameStarted    = true;
  paused         = false;
  matchElapsedMs = 0;
  matchStartMs   = performance.now();
  accumulator    = 0;   // don't simulate the time spent on the landing page
}

// Ends the run and hands the player straight back to the start screen, which
// reports the time just set. One click from there is a new match — no separate
// win dialog to dismiss first. Whatever is still held is released, so letting
// go of a button on the start screen fires nothing, and the continuous sounds
// stop with the simulation.
function winMatch(): void {
  if (gameWon) return;
  gameWon     = true;
  finalTimeMs = matchTimeMs();
  lastRunMs   = finalTimeMs;
  wonWithBest = recordScore(finalTimeMs);
  gameStarted = false;
  releaseInputs();
  fadeOutSkiAudio();
  for (const b of balls) stopBallRoll(b);
}

// Rebuilds the world for another run. Terrain is regenerated and the object
// arrays are emptied; trees, boxes and gulls repopulate themselves from the
// null frontiers on the next tick.
function resetMatch(): void {
  for (const b of balls) stopBallRoll(b);
  balls.length  = 0;
  pops.length   = 0;
  bursts.length = 0;
  trees.length  = 0;
  boxes.length  = 0;
  gulls.length  = 0;
  playerBalloons = [];
  popFrontierRight = null;
  popFrontierLeft  = null;

  initTerrain();
  player.x = 0;
  player.y = terrainY(0) - player.radius;
  player.vx = 0;
  player.vy = 0;
  centreCamera();

  onGround = false;
  touchingGround = false;
  floatAnchorY = null;
  floatDelay = FLOAT_DELAY;
  isSkiing = false;
  groundSurface = 'grass';
  stepAccum = 0;
  meterFill = 0;
  meterPulse = 0;
  releaseInputs();
  fadeOutSkiAudio();

  gameWon     = false;
  finalTimeMs = null;
}

// ─── Scores ───────────────────────────────────────────────────────────────────
// Two five-entry lists in localStorage: the fastest runs and the latest runs.
// A shorter time is a better one, so "best" sorts ascending.
const SCORES_KEY: string  = 'jaunt.scores.v1';
const SCORE_SLOTS: number = 5;

interface ScoreEntry { ms: number; at: number }   // a finished run's time, and when it was set
interface Scores { best: ScoreEntry[]; recent: ScoreEntry[] }

// Whether a parsed value is an object whose properties can be looked at.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// One stored entry, if it has a usable time. The date is optional in storage
// (dayLabel shows nothing for a missing one), so it is carried as NaN.
function toScoreEntry(value: unknown): ScoreEntry | null {
  if (!isRecord(value)) return null;
  const ms = value['ms'];
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  const at = value['at'];
  return { ms, at: typeof at === 'number' ? at : NaN };
}

function loadScores(): Scores {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SCORES_KEY) ?? 'null');
    const clean = (list: unknown): ScoreEntry[] => (Array.isArray(list) ? list : [])
      .map(toScoreEntry)
      .filter((entry): entry is ScoreEntry => entry !== null)
      .slice(0, SCORE_SLOTS);
    const lists = isRecord(parsed) ? parsed : {};
    return { best: clean(lists['best']), recent: clean(lists['recent']) };
  } catch {
    return { best: [], recent: [] };   // unreadable or unavailable storage: start clean
  }
}

function saveScores(): void {
  try {
    localStorage.setItem(SCORES_KEY, JSON.stringify(scores));
  } catch {
    // Private mode or a full quota: the run still counts for this session.
  }
}

// Files the finished run into both lists; returns true if it is the new best.
function recordScore(ms: number): boolean {
  const entry: ScoreEntry = { ms, at: Date.now() };
  scores.recent = [entry, ...scores.recent].slice(0, SCORE_SLOTS);
  scores.best   = [...scores.best, entry]
    .sort((a, b) => a.ms - b.ms)
    .slice(0, SCORE_SLOTS);
  saveScores();
  return scores.best[0] === entry;
}

const scores: Scores = loadScores();

// ─── Landing page ─────────────────────────────────────────────────────────────
// Shown over a still frame of the world before the first tick: the two score
// lists and a Start button. Clicking Start clears it and starts the clock.
const LANDING_W: number = 560;
const LANDING_H: number = 448;

function landingRect(): Rect {
  return {
    x: uiW()  / 2 - LANDING_W / 2,
    y: uiH() / 2 - LANDING_H / 2,
    w: LANDING_W,
    h: LANDING_H,
  };
}

function startButtonRect(): Rect {
  const p = landingRect();
  const w = 200, h = 56;
  return { x: uiW() / 2 - w / 2, y: p.y + p.h - h - 34, w, h };
}

function hitsRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function dayLabel(at: number): string {
  if (!Number.isFinite(at)) return '';
  const d = new Date(at);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// One column of the landing page. Ranked lists number their rows and gild the
// leader; the recent list dates them instead.
function drawScoreList(title: string, list: ScoreEntry[], x: number, y: number, w: number, ranked: boolean): void {
  const ROW_H = 26;
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = 'bold 14px monospace';
  ctx.fillText(title, x + w / 2, y);

  ctx.beginPath();
  ctx.moveTo(x + 14, y + 11);
  ctx.lineTo(x + w - 14, y + 11);
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 1;
  ctx.stroke();

  if (list.length === 0) {
    ctx.fillStyle = 'rgba(255,255,255,0.30)';
    ctx.font = '14px monospace';
    ctx.fillText('no runs yet', x + w / 2, y + 36);
    return;
  }

  ctx.font = '16px monospace';
  for (const [i, entry] of list.entries()) {
    const ry = y + 36 + i * ROW_H;
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillText(ranked ? `${i + 1}.` : dayLabel(entry.at), x + 20, ry);
    ctx.textAlign = 'right';
    ctx.fillStyle = (ranked && i === 0) ? '#ffdd00' : '#ffffff';
    ctx.fillText(formatTime(entry.ms), x + w - 20, ry);
  }
}

function drawStartScreen(): void {
  beginUI();
  ctx.save();

  // Dim the world behind the panel
  ctx.fillStyle = 'rgba(0,0,20,0.72)';
  ctx.fillRect(0, 0, uiW(), uiH());

  const p = landingRect();
  ctx.fillStyle = '#1a1a3a';
  ctx.strokeStyle = '#ffdd00';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(p.x, p.y, p.w, p.h, 16);
  ctx.fill(); ctx.stroke();

  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffdd00';
  ctx.font = 'bold 54px monospace';
  ctx.fillText('Jaunt', uiW() / 2, p.y + 72);

  // After a run, the screen that sends you back out is also the one that tells
  // you how you did.
  if (lastRunMs !== null) {
    ctx.font = '16px monospace';
    ctx.fillStyle = wonWithBest ? '#ffdd00' : 'rgba(255,255,255,0.75)';
    const line = wonWithBest
      ? 'your time was ' + formatTime(lastRunMs) + ' — new best!'
      : 'your time was ' + formatTime(lastRunMs);
    ctx.fillText(line, uiW() / 2, p.y + 104);
  }

  const colW = (p.w - 72) / 2;
  drawScoreList('BEST TIMES', scores.best,   p.x + 36,        p.y + 140, colW, true);
  drawScoreList('RECENT',     scores.recent, p.x + 36 + colW, p.y + 140, colW, false);

  const b = startButtonRect();
  ctx.fillStyle = '#4488ff';
  ctx.beginPath();
  ctx.roundRect(b.x, b.y, b.w, b.h, 10);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 26px monospace';
  ctx.textBaseline = 'middle';
  ctx.fillText('Start', uiW() / 2, b.y + b.h / 2 + 1);

  ctx.restore();
  endUI();
}

// The start screen owns the only button in the game: it opens the first match
// and every one after it.
canvas.addEventListener('click', e => {
  if (gameStarted) return;
  const p = toUI(e.clientX, e.clientY);
  if (!hitsRect(startButtonRect(), p.x, p.y)) return;
  pressStart();
});

// ─── Loop ─────────────────────────────────────────────────────────────────────
const TICK_RATE: number = 1000 / 60;
// A frame that ran long — a collection pause, a slow paint, the phone throttling
// — must not turn into a burst of catch-up ticks that makes the next frame later
// still, and the one after that later again. Past this many frames' worth, the
// missed time is dropped rather than simulated.
const MAX_CATCH_UP_TICKS: number = 5;
let lastTime: number = 0;
let accumulator: number = 0;

function loop(timestamp: number): void {
  const delta = timestamp - lastTime;
  lastTime = timestamp;

  // Time only runs during a match that is not paused; startMatch and
  // resumeGame clear the accumulator, so nothing waiting is simulated later.
  if (gameStarted && !paused) {
    accumulator = Math.min(accumulator + delta, TICK_RATE * MAX_CATCH_UP_TICKS);
    while (accumulator >= TICK_RATE) { if (!gameWon) update(); accumulator -= TICK_RATE; }
  }

  beginView();
  draw();
  if (!gameStarted)  drawStartScreen();
  else if (paused)   drawPauseScreen();
  endView();
  if (touchMode && gameStarted && !paused) drawTouchPad();

  requestAnimationFrame(loop);
}

function drawPauseScreen(): void {
  beginUI();
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, uiW(), uiH());
  const w = 360, h = 150;
  const x = uiW() / 2 - w / 2, y = uiH() / 2 - h / 2;
  ctx.fillStyle = '#1a1a2e';
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(x, y, w, h, 14); ctx.fill(); ctx.stroke();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 34px monospace';
  ctx.fillText('Paused', uiW() / 2, y + 55);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '18px monospace';
  ctx.fillText(touchMode ? 'tap to resume' : 'click to resume', uiW() / 2, y + 105);
  ctx.restore();
  endUI();
}

requestAnimationFrame(loop);
